// QuoteLog.jsx
// Quote log tab — reads from Google Sheets, shows all past quotes
// with status management, filtering, search, and amendment tracking

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useUser } from "@clerk/react";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import RecordPage from "./RecordPage";
import JobNotes from "./JobNotes";
import AdminBccToggles, { adminBccList } from "./AdminBccToggles";
import NoAutofillInput from "./NoAutofillInput";
import { SectorPill, SectorIcon, getSector } from "./sectors";
import { resolveDiscount, generateQuoteDocumentHTML } from "./generateQuoteDocument";
import { printHTML } from "./jobSheetFromQuote";
import { hasDiscount } from "./copyQuote";
import { resolveClientScope, scopeTextToHTML } from "./scopeTemplates";
import { presetPersonalNote } from "./discountPresets";
import {
  fetchAllQuotes,
  updateQuoteStatus,
  markQuoteLost,
  generateAmendmentNumber,
  sendQuoteEmail,
  recordClientSend,
  fetchStatusLog,
  isInternalOnlySend,
  TEST_QUOTE_SEND_BLOCKED,
  logStatusChange,
  fetchSuppliers,
  parsePOData,
  savePODetails,
  computeOrderByDate,
  computePOTotalCost,
  findDryIceSuppliers,
  primaryDryIceSupplier,
  hasDeliveredTiers,
  fetchCompanySettings,
  sendPurchaseOrderEmail,
  extractCalculatorDryIceKg,
  fmtKg,
  getQuoteContactName,
  emailGreeting,
  AUTO_EMAIL_BLOCKS,
  defaultAutoEmailBlocks,
  getQuoteVehicleMake,
  fetchSendLog,
  lastSendFor,
  versionClientHas,
  quotePhone,
  clientHasLabel,
  isOnFileRow,
  formatUkTimestamp,
  quotePageUrl,
  parseDate,
  getJobDates,
  getScheduledDate,
  withJobDates,
  saveJobDates,
  toISODate,
  markPOConfirmed,
  markQuoteSentManually,
  apiFetch,
  normaliseTimestamp,
  updateContactDetails,
} from "./quoteLogger";
import { C, TONE, fmtMoney, baseNumber, versionOf, isTestQuote, TestTag, VersionTag, StatusBadge } from "./quoteUi";
import { ACTIVE_STATUSES, COMPLETED_STATUSES, CLOSED_STATUSES, JOB_STATUSES, tabOfStatus, manualTransitions, isAutomotive, isLostStatus, lossReasonOf, conversionStats, latestStatusNote, overlappingJobs, LOSS_REASONS, REASON_NOT_RECORDED, DEPOSIT_PENDING, DEPOSIT_PAID, LOST } from "./workflow";
// CompanySettingsPage imports the light theme from here.
export { C };

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (v) => (v ? v : "—");
const fmtDate = (d) => (d ? d : "—");
// Compact table date: "18 Sep" this year, "18 Sep 2025" otherwise. Hover
// shows the full date.
const fmtShortDate = (d) => {
  const dt = parseDate(d);
  if (!dt) return fmtDate(d);
  const sameYear = dt.getFullYear() === new Date().getFullYear();
  return dt.toLocaleDateString("en-GB", sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
};
// Sort key for a sheet date cell - unparseable/blank sorts as oldest.
const dateValue = (d) => parseDate(d)?.getTime() ?? 0;

// ─── List sort ───────────────────────────────────────────────────────────────
const SORT_STORAGE_KEY = "sublime_quotelog_sort";
// VAT rule: every internal figure is ex VAT; VAT appears only on the client
// quote's (and invoice's) final totals.
const SORTABLE = ["Quote Number", "Date Created", "Date Updated", "Client Name", "Category", "Application", "Total (ex VAT)", "Status"];
const DEFAULT_SORT = { key: "Date Updated", dir: "desc" };
function loadSort() {
  try {
    const v = JSON.parse(window.localStorage.getItem(SORT_STORAGE_KEY) || "null");
    if (v && SORTABLE.includes(v.key) && (v.dir === "asc" || v.dir === "desc")) return v;
  } catch (e) { /* storage blocked or bad value */ }
  return DEFAULT_SORT;
}
// Public quote-preview page — same one linked from the client email, opened
// in a new tab wherever a quote's actual content needs checking (before
// sending, or independently from the detail panel).
const quotePreviewUrl = (quoteNumber) => quotePageUrl(quoteNumber);

// Sent this many days ago with no reply: "follow up or mark lost".
const FOLLOW_UP_OR_LOST_DAYS = 21;

// ─── Active / Completed / Closed ────────────────────────────────────────────
// Tabs and status groups live in workflow.js: Active = everything still in
// play (Draft through Invoiced, incl. Automotive's deposit stages); Completed
// = Complete (the job's done and paid); Closed = Lost (old rows: Rejected).
// Pipeline order for the Status sort.
const PIPELINE_STATUSES = ["Draft", "Sent", "Amended", "Accepted", DEPOSIT_PENDING, DEPOSIT_PAID, "Contracted", "Invoiced", "Complete", LOST, "Rejected"];
const statusesOfView = v => v === "closed" ? CLOSED_STATUSES : v === "completed" ? COMPLETED_STATUSES : ACTIVE_STATUSES;


// ─── Small line icons (no external icon library) ────────────────────────────

// ─── Send to Client confirmation modal ────────────────────────────────────────

// A dashboard section that can be folded away; remembered per browser.
function Collapsible({ id, title, right, children, isMobile }) {
  const key = `sublime_dash_open_${id}`;
  const [open, setOpen] = useState(() => { try { return window.localStorage.getItem(key) !== "0"; } catch (e) { return true; } });
  const toggle = () => setOpen(o => { try { window.localStorage.setItem(key, o ? "0" : "1"); } catch (e) { /* storage blocked */ } return !o; });
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: isMobile ? 16 : 22, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <button onClick={toggle} aria-expanded={open} style={{ display: "flex", alignItems: "center", gap: 8, border: "none", background: "none", padding: 0, cursor: "pointer", fontSize: 13, fontWeight: 700, color: C.text }}>
          <span style={{ color: C.textFaint, fontSize: 11, width: 10 }}>{open ? "▾" : "▸"}</span>{title}
        </button>
        {right}
      </div>
      {open && <div style={{ marginTop: 14 }}>{children}</div>}
    </div>
  );
}

function parseStateJson(quote) {
  try { return JSON.parse(quote["State JSON"] || "null"); } catch (e) { return null; }
}

// Send buttons while a contact edit is saving: a send then could still go to
// the old address.
const SAVING_CONTACT = "Saving contact details…";

// Contact person from a logged row's State JSON (see getQuoteContactName).
function quoteContactName(quote) {
  return getQuoteContactName(parseStateJson(quote));
}

// Small light dialog shell for the status dialogs below.
function StatusDialogShell({ title, children, onClose, busy }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(10,20,50,0.45)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div role="dialog" aria-modal="true" style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%", maxWidth: 440, boxSizing: "border-box", fontFamily: "'IBM Plex Sans', sans-serif", color: "#1a1a2e" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
const dialogLabel = { fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 4 };
const dialogInput = { width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid #d0d8e8", fontSize: 13, boxSizing: "border-box", fontFamily: "'IBM Plex Sans', sans-serif", color: "#1a1a2e", background: "#fff" };
const dialogBtn = primary => ({ flex: 1, minHeight: 42, borderRadius: 9, border: primary ? "none" : "1px solid #d0d8e8", background: primary ? "#1D3461" : "#fff", color: primary ? "#fff" : "#1a1a2e", fontSize: 14, fontWeight: 700, cursor: "pointer" });

// Mark lost: a reason (required, one of LOSS_REASONS) and an optional note.
function LostDialog({ quote, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  return (
    <StatusDialogShell title={`Mark ${quote["Quote Number"]} lost`} onClose={onClose} busy={busy}>
      <label style={dialogLabel}>Why was it lost?</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {LOSS_REASONS.map(r => (
          <button key={r} type="button" onClick={() => setReason(r)} aria-pressed={reason === r}
            style={{ padding: "7px 12px", borderRadius: 999, border: `1px solid ${reason === r ? "#1D3461" : "#d0d8e8"}`, background: reason === r ? "#1D3461" : "#fff", color: reason === r ? "#fff" : "#1a1a2e", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{r}</button>
        ))}
      </div>
      <label style={dialogLabel}>Note (optional)</label>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="e.g. went with a local garage at £900" style={{ ...dialogInput, resize: "vertical", marginBottom: 14 }} />
      <div style={{ display: "flex", gap: 10 }}>
        <button type="button" style={{ ...dialogBtn(true), opacity: reason ? 1 : 0.5, cursor: reason ? "pointer" : "default" }} disabled={!reason || busy} onClick={() => onConfirm({ reason, note: note.trim() })}>{busy ? "Saving…" : "Mark lost"}</button>
        <button type="button" style={dialogBtn(false)} onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </StatusDialogShell>
  );
}

// Deposit invoice sent / Mark deposit paid / Mark complete: optional note.
const NOTE_CHANGE_TEXT = {
  [DEPOSIT_PENDING]: { title: "Deposit invoice sent", button: "Deposit invoice sent", history: "Deposit invoice sent" },
  [DEPOSIT_PAID]: { title: "Mark deposit paid", button: "Mark deposit paid", history: "Deposit paid" },
  Complete: { title: "Mark complete", button: "Mark complete", history: "Complete, balance paid on collection" },
};
function StatusNoteDialog({ quote, newStatus, busy, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const txt = NOTE_CHANGE_TEXT[newStatus] || { title: `Mark ${newStatus}`, button: `Mark ${newStatus}`, history: "" };
  return (
    <StatusDialogShell title={`${txt.title}: ${quote["Quote Number"]}`} onClose={onClose} busy={busy}>
      <label style={dialogLabel}>Note (optional)</label>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder={newStatus === DEPOSIT_PAID ? "e.g. £750 by bank transfer" : "Anything worth keeping"} style={{ ...dialogInput, resize: "vertical", marginBottom: 14 }} />
      <div style={{ display: "flex", gap: 10 }}>
        <button type="button" style={dialogBtn(true)} disabled={busy} onClick={() => onConfirm([txt.history, note.trim()].filter(Boolean).join(": "))}>{busy ? "Saving…" : txt.button}</button>
        <button type="button" style={dialogBtn(false)} onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </StatusDialogShell>
  );
}

// One-off: old "Rejected" rows become Lost with "Reason not recorded".
// Lists every row it will change before anything is written.
function ConvertRejectedDialog({ rows, busy, done, onClose, onConfirm }) {
  return (
    <StatusDialogShell title="Convert Rejected to Lost" onClose={onClose} busy={busy}>
      <div style={{ fontSize: 13, color: "#4a5568", marginBottom: 10 }}>
        {rows.length} row{rows.length === 1 ? "" : "s"} will change Status from Rejected to Lost, with Loss Reason "{REASON_NOT_RECORDED}" and a line in each one's History. Nothing else on the row changes.
      </div>
      <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #eef0f6", borderRadius: 8, marginBottom: 14 }}>
        {rows.map(q => (
          <div key={q["Quote Number"]} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 10px", borderBottom: "1px solid #f3f4f8", fontSize: 13 }}>
            <span><strong>{q["Quote Number"]}</strong> · {q["Client Name"] || "No client"}{q["Test Quote"] === "Yes" ? " · test" : ""}</span>
            <span style={{ color: "#8a95a3" }}>{q["Total (ex VAT)"]}{done.includes(q["Quote Number"]) ? " ✓" : ""}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button type="button" style={dialogBtn(true)} disabled={busy || done.length === rows.length} onClick={onConfirm}>{busy ? `Converting… ${done.length} of ${rows.length}` : done.length === rows.length ? "Done" : `Convert ${rows.length}`}</button>
        <button type="button" style={dialogBtn(false)} onClick={onClose} disabled={busy}>{done.length === rows.length ? "Close" : "Cancel"}</button>
      </div>
    </StatusDialogShell>
  );
}

function SendToClientModal({ quote, sending, onClose, onConfirm }) {
  const [clientName, setClientName] = useState(quote["Client Name"] || "");
  const contactName = quoteContactName(quote);
  const isAutoQuote = quote["Category"] === "Automotive";
  const quoteState = parseStateJson(quote);
  const exVatTotal = parseFloat(String(quote["Total (ex VAT)"] || "").replace(/[^0-9.]/g, "")) || 0;
  // A discount preset (e.g. Showcase Project) pre-fills this; still editable.
  const [personalNote, setPersonalNote] = useState(() => presetPersonalNote(quoteState, resolveDiscount(quoteState?.commercial, exVatTotal)));
  const vehicleMake = getQuoteVehicleMake(quoteState);
  const [emailBlocks, setEmailBlocks] = useState(() => defaultAutoEmailBlocks(quoteState));
  const [email, setEmail] = useState(quote["Client Email"] || "");
  // Quote emails: BCC Edward by default, James opt-in.
  const [bcc, setBcc] = useState({ edward: true, james: false });
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const labelStyle = {
    fontSize: 11, color: "#666", textTransform: "uppercase",
    letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 4,
  };
  const inputStyle = {
    width: "100%", padding: "9px 12px", borderRadius: 6,
    border: "1px solid #d0d8e8", fontSize: 13, boxSizing: "border-box",
    fontFamily: "'IBM Plex Sans', sans-serif", outline: "none",
    color: "#1a1a2e", background: "#fff",
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(10,20,50,0.45)",
        zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onClick={e => { if (e.target === e.currentTarget && !sending) onClose(); }}
    >
      <div style={{
        background: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 420,
        fontFamily: "'IBM Plex Sans', sans-serif", position: "relative",
        boxShadow: "0 12px 48px rgba(0,0,0,0.22)",
      }}>
        <button
          onClick={onClose}
          disabled={sending}
          style={{
            position: "absolute", top: 14, right: 14, background: "none",
            border: "none", fontSize: 18, cursor: sending ? "default" : "pointer",
            color: "#999", lineHeight: 1, padding: 4,
          }}
        >
          ✕
        </button>

        <h3 style={{ margin: "0 0 6px", fontSize: 19, color: "#1D3461", fontWeight: 700 }}>
          Send Quote to Client
        </h3>
        <div style={{
          fontSize: 13, color: "#555", marginBottom: 20,
          padding: "8px 12px", background: "#f0f4ff", borderRadius: 6,
          display: "flex", gap: 12, flexWrap: "wrap",
        }}>
          <span style={{ fontWeight: 700, color: "#1D3461" }}>{quote["Quote Number"]}</span>
          {quote["Category"] && <SectorPill category={quote["Category"]} />}
          {quote["State JSON"] && (
            <a
              href={quotePreviewUrl(quote["Quote Number"])}
              target="_blank"
              rel="noreferrer"
              style={{ marginLeft: "auto", color: "#3a52cc", fontWeight: 600, textDecoration: "none" }}
            >
              Preview Quote ↗
            </a>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>Client Name</label>
            <NoAutofillInput
              style={inputStyle}
              name="sp-clientname" value={clientName}
              onChange={e => setClientName(e.target.value)}
              placeholder="Client name"
              disabled={sending}
            />
            <div style={{ fontSize: 12, color: "#8ab0cc", marginTop: 6 }}>
              Email opens with “{emailGreeting(contactName, clientName)}”{contactName ? "" : " (no contact name on this quote)"}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Send To (Email)</label>
            <NoAutofillInput
              style={inputStyle}
              type="email"
              name="sp-email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Type the client's email" className="sp-hint"
              disabled={sending}
            />
            {!emailValid && email.trim() !== "" && (
              <div style={{ fontSize: 12, color: "#8B1A1A", marginTop: 6 }}>Enter a valid email address.</div>
            )}
            {email.trim() === "" && (
              <div style={{ fontSize: 12, color: "#8ab0cc", marginTop: 6 }}>No email was on file for this quote — add one to send.</div>
            )}
            <div style={{ marginTop: 8 }}>
              <AdminBccToggles compact value={bcc} onChange={setBcc} disabled={sending} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Custom email paragraph (optional)</label>
            <textarea
              style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
              value={personalNote}
              onChange={e => setPersonalNote(e.target.value)}
              disabled={sending}
            />
            <div style={{ fontSize: 12, color: "#8ab0cc", marginTop: 6 }}>Your own wording for this client, added between the greeting and the quote summary.</div>
          </div>

          {/* Optional Automotive email blocks - only ticked ones are sent. */}
          {isAutoQuote && (
            <div>
              <label style={labelStyle}>Include in Email</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {AUTO_EMAIL_BLOCKS.map(b => (
                  <label key={b.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1a1a2e", cursor: sending ? "default" : "pointer" }}>
                    <input type="checkbox" checked={!!emailBlocks[b.key]} disabled={sending} onChange={e => setEmailBlocks(v => ({ ...v, [b.key]: e.target.checked }))} />
                    {b.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              flex: 1, padding: "11px", borderRadius: 6,
              border: "1px solid #d0d8e8", background: "#fff",
              color: "#666", fontSize: 13, cursor: sending ? "default" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ clientName: clientName.trim(), email: email.trim(), bcc: adminBccList(bcc), personalNote, blocks: emailBlocks, vehicleMake })}
            disabled={sending || !emailValid}
            style={{
              flex: 2, padding: "11px", borderRadius: 6, border: "none",
              background: sending ? "#8fa3c4" : !emailValid ? "#c3cfe0" : "#3a52cc",
              color: "#fff", fontSize: 13, fontWeight: 700,
              cursor: (sending || !emailValid) ? "default" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {sending ? "Sending…" : "✓ Confirm & Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Raise PO (draft/approve/send) ──────────────────────────────────────────
// Manually triggered for now (a "Raise PO" button on the quote's Purchase
// Order section) - everything here is editable right up to the moment of
// sending, not a plain yes/no confirmation. Quantity is a manual input at
// this stage; auto-pulling it from the job's calculator inputs is a later
// build stage, so this doubles as that stage's "manual fallback" from day
// one rather than sitting unimplemented until then.
function RaisePOModal({ quote, poDraft, suppliers, approverName, onClose, onApproved }) {
  const [supplierId, setSupplierId] = useState(poDraft?.supplierId || primaryDryIceSupplier(findDryIceSuppliers(suppliers))?.id || "");
  // Optional delivered-tier extras (e.g. Saturday delivery), ticked per PO.
  const [extras, setExtras] = useState([]);
  const [deliveryDestination, setDeliveryDestination] = useState(poDraft?.deliveryDestination || "Yard");
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(poDraft?.requestedDeliveryDate || "");
  // Quantity primarily comes from the job's own calculator (the
  // wastage-adjusted dry-ice figure saved into State JSON), not a manual
  // guess - autoQtyKg is fixed at modal open (the quote prop doesn't change
  // during its lifetime) and only used to seed the field and drive the
  // "where did this number come from" caption below; the field itself
  // stays freely editable afterward.
  const autoQtyKg = extractCalculatorDryIceKg(quote);
  const [quantityKg, setQuantityKg] = useState(autoQtyKg != null ? fmtKg(autoQtyKg) : "");
  // Defaults checked - unlike the client-quote send (where admin visibility
  // is a nice-to-have), a real supplier order is something both admins
  // would normally want to see land. Independent toggles (not one combined
  // checkbox) since either admin might want to opt out on a given order
  // without affecting the other.
  const [bcc, setBcc] = useState({ edward: true, james: true });
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);

  useEffect(() => {
    fetchCompanySettings().then(setCompanySettings).catch(() => {});
  }, []);

  const supplier = suppliers.find(s => s.id === supplierId) || null;
  // Editable "To" field, separate from supplier.orderEmail - pre-filled
  // from the supplier's saved Order Email Address but changeable per send,
  // and re-synced (not just filled-if-blank) whenever the supplier
  // selection itself changes, since a different supplier's default email
  // is the only sane starting point once you've switched.
  const [orderEmail, setOrderEmail] = useState(supplier?.orderEmail || "");
  useEffect(() => {
    setOrderEmail(supplier?.orderEmail || "");
    setExtras([]);
  }, [supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback only - if the job has no calculator-derived quantity (e.g. an
  // older quote from before this was tracked), pre-fill from the
  // supplier's minimum order instead of leaving the field empty. Never
  // overrides an auto-pulled or already-typed value.
  useEffect(() => {
    if (autoQtyKg == null && supplier?.minimumOrderKg && !quantityKg) setQuantityKg(supplier.minimumOrderKg);
  }, [supplier]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedAddress = deliveryDestination === "Site"
    ? (quote["Site Address"] || "—")
    : (supplier?.yardDeliveryAddress || "—");
  // costResult.plan is chosen by chooseBinPlan() off the real required
  // quantity (quantityKg) - productCost is billed on that same real
  // requirement, never on the bin-rounded figure below. quantityOrderedKg
  // is a separate, larger-or-equal number: what a full bin configuration
  // actually holds (binsRequired × capacityKg) - shown alongside the
  // required figure so it's clear how much of the order is genuine job
  // need vs. rounding up to fill a bin.
  // A delivered-tier supplier orders the tier's kg instead (costResult.tiered).
  const tiered = hasDeliveredTiers(supplier);
  const costResult = supplier && quantityKg ? computePOTotalCost({ quantityOrderedKg: quantityKg, supplier, extras }) : null;
  const quantityOrderedKg = costResult ? costResult.orderedKg : null;
  // Surplus = ordered − the calculator's required figure (what the job
  // actually needs), falling back to the entered quantity when the job has
  // no calculator figure. Stored with the PO at send time, 1 decimal place.
  const surplusKg = quantityOrderedKg != null
    ? Math.max(0, Math.round((quantityOrderedKg - (autoQtyKg ?? Number(quantityKg))) * 10) / 10)
    : null;
  const poRef = `PO-${quote["Quote Number"]}`;

  async function handleApprove() {
    // Guards against ever sending a real supplier email with a garbled
    // "PO-undefined" reference - if the linked quote number isn't
    // available for some reason, fail loudly here rather than silently
    // sending a bad reference.
    if (!quote["Quote Number"]) { setError("This quote has no Quote Number on file — cannot raise a PO without one."); return; }
    if (!supplier) { setError("Select a supplier before approving."); return; }
    if (!orderEmail.trim()) { setError(`Enter an order email address to send to${supplier.name ? ` for ${supplier.name}` : ""}.`); return; }
    if (!requestedDeliveryDate) { setError("Set a Requested Delivery Date before approving."); return; }
    if (!quantityKg || Number(quantityKg) <= 0) { setError("Enter a quantity before approving."); return; }
    if (!costResult) { setError(`${supplier.name} has no ${tiered ? "delivered tiers" : "bin sizes"} set up in Admin — add at least one before approving.`); return; }

    setApproving(true);
    setError(null);
    try {
      await sendPurchaseOrderEmail({
        to: orderEmail.trim(),
        poRef,
        supplierName: supplier.name,
        accountNumber: supplier.accountNumber,
        deliveryAddress: resolvedAddress,
        requestedDeliveryDate,
        quantityKg,
        binPlan: costResult.plan,
        tierPlan: costResult.tierPlan || null,
        extras: costResult.extras || [],
        approverName,
        companyPhone: companySettings?.phoneNumber,
        companyEmail: companySettings?.companyEmail,
        bcc: adminBccList(bcc),
        // Same cost breakdown already shown in the Order Summary above,
        // on the record in the PO email itself - Nippon has no credit
        // account, so this email doubles as payment authorisation.
        unitCostPerKg: tiered ? null : supplier.currentCostPricePerKg,
        productCost: costResult.productCost,
        rentalCost: costResult.rentalCost,
        deliveryCost: costResult.deliveryCost,
        totalCost: costResult.totalCost,
        paymentTerms: supplier.paymentTerms,
        // Company identity pulled from Admin -> Settings (single source of
        // truth), not hardcoded here.
        companyName: companySettings?.companyName,
        tradingAs: companySettings?.tradingAs,
        registeredAddress: companySettings?.registeredAddress,
        companyNumber: companySettings?.companyNumber,
        vatNumber: companySettings?.vatNumber,
      });

      // Saved snapshot, not recalculated later - deliberately captures the
      // exact figures the email actually went out with (price, bin plan,
      // total cost), so this stays accurate even if the supplier's price
      // changes afterward.
      const poData = withJobDates(parsePOData(quote), {
        supplierId, deliveryDestination, requestedDeliveryDate,
        quantityRequiredKg: Number(quantityKg),
        quantityOrderedKg,
        surplusKg,
        quantitySource: autoQtyKg != null ? "calculator" : "manual",
        poStatus: "sent",
        resolvedDeliveryAddress: resolvedAddress,
        unitCostPerKg: tiered ? null : supplier.currentCostPricePerKg,
        binPlan: costResult.plan,
        ...(tiered ? { tierPlan: costResult.tierPlan, extras: costResult.extras } : {}),
        totalCost: costResult.totalCost,
        approvedBy: approverName,
        poSentTimestamp: new Date().toISOString(),
      });
      await savePODetails(quote._rowIndex, poData);
      onApproved(poData);
    } catch (e) {
      setError("Failed to send PO: " + e.message);
    } finally {
      setApproving(false);
    }
  }

  const labelStyle = { fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 4 };
  const inputStyle = { width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid #d0d8e8", fontSize: 13, boxSizing: "border-box", fontFamily: "'IBM Plex Sans', sans-serif", outline: "none", color: "#1a1a2e", background: "#fff" };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(10,20,50,0.45)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget && !approving) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto", fontFamily: "'IBM Plex Sans', sans-serif", position: "relative", boxShadow: "0 12px 48px rgba(0,0,0,0.22)" }}>
        <button onClick={onClose} disabled={approving} style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", fontSize: 18, cursor: approving ? "default" : "pointer", color: "#999", lineHeight: 1, padding: 4 }}>✕</button>

        <h3 style={{ margin: "0 0 4px", fontSize: 19, color: "#1D3461", fontWeight: 700 }}>Raise Purchase Order</h3>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>{poRef} — {quote["Quote Number"]}</div>

        {error && (
          <div style={{ padding: "10px 12px", background: "#FEECEC", color: "#8B1A1A", borderRadius: 6, marginBottom: 16, fontSize: 13, border: "1px solid #E53935" }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>Supplier</label>
            <select style={{ ...inputStyle, cursor: "pointer" }} value={supplierId} onChange={e => setSupplierId(e.target.value)}>
              <option value="">Select supplier…</option>
              {suppliers.filter(s => s.active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Order Email (To)</label>
            <input type="email" style={inputStyle} value={orderEmail} onChange={e => setOrderEmail(e.target.value)} placeholder="orders@supplier.com" />
            <div style={{ fontSize: 11, color: "#8a95a3", marginTop: 5 }}>
              {supplier
                ? (supplier.orderEmail ? `Pre-filled from ${supplier.name}'s saved Order Email — edit if this order should go elsewhere.` : `${supplier.name} has no Order Email Address saved in Admin — enter one to send.`)
                : "Select a supplier to pre-fill this, or enter an address directly."}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Delivery Destination</label>
            <div style={{ display: "flex", gap: 6 }}>
              {["Yard", "Site"].map(dest => (
                <button
                  key={dest}
                  onClick={() => setDeliveryDestination(dest)}
                  style={{
                    flex: 1, padding: "8px 0", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer",
                    border: deliveryDestination === dest ? "none" : "1px solid #d0d8e8",
                    background: deliveryDestination === dest ? "#3a52cc" : "transparent",
                    color: deliveryDestination === dest ? "#fff" : "#4a6483",
                  }}
                >
                  {dest}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>{resolvedAddress}</div>
            {deliveryDestination === "Site" && !tiered && (
              <div style={{ marginTop: 8, padding: "8px 12px", background: "#FEF3E1", border: "1px solid #F5D28C", borderRadius: 6, fontSize: 12, color: "#93630A" }}>
                ⚠ The delivery surcharge below is {supplier?.name || "the supplier"}'s Yard/TR27 rate — it may not be correct for this address. Treat it as an estimate and confirm with the supplier if it matters for this order.
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Requested Delivery Date</label>
            <input type="date" style={inputStyle} value={requestedDeliveryDate} onChange={e => setRequestedDeliveryDate(e.target.value)} />
          </div>

          <div>
            <label style={labelStyle}>Quantity Required (kg)</label>
            <input type="number" style={inputStyle} value={quantityKg} onChange={e => setQuantityKg(e.target.value)} placeholder={supplier?.minimumOrderKg ? `Minimum order ${supplier.minimumOrderKg}kg` : ""} />
            <div style={{ fontSize: 11, color: "#8a95a3", marginTop: 5 }}>
              {autoQtyKg != null
                ? `Auto-pulled from this job's calculator (${fmtKg(autoQtyKg)}kg, adjusted for wastage) — edit if needed.`
                : "No calculator quantity found on this job — enter manually."}
            </div>
          </div>

          {tiered && (supplier.deliveredExtras || []).length > 0 && (
            <div>
              <label style={labelStyle}>Delivery Extras</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {supplier.deliveredExtras.map(x => (
                  <label key={x.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1a1a2e", cursor: "pointer" }}>
                    <input type="checkbox" checked={extras.includes(x.label)} onChange={e => setExtras(v => e.target.checked ? [...v, x.label] : v.filter(l => l !== x.label))} />
                    {x.label} (+£{Number(x.price).toFixed(2)})
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {costResult && (
          <div style={{ marginTop: 18, padding: "14px 16px", background: "#F7F8FC", border: "1px solid #e0e6f0", borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8ab0cc", marginBottom: 8 }}>Order Summary</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#555", padding: "2px 0" }}>
              <span>Quantity Required (job need)</span>
              <span>{fmtKg(quantityKg)}kg</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#555", padding: "2px 0" }}>
              <span>Quantity Ordered ({tiered ? "tier" : "bin-rounded"})</span>
              <span>{fmtKg(quantityOrderedKg)}kg</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#555", padding: "2px 0", marginBottom: 8 }}>
              <span>Surplus{autoQtyKg != null ? " (vs calculator figure)" : ""}</span>
              <span>{fmtKg(surplusKg)}kg</span>
            </div>
            <div style={{ fontSize: 13, color: "#1a1a2e", marginBottom: 10 }}>
              {costResult.planLabel}
            </div>
            {costResult.lines.map(([label, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#555", padding: "2px 0" }}>
                <span>{label}</span>
                <span>£{Number(val).toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, color: "#1D3461", marginTop: 8, paddingTop: 8, borderTop: "1px solid #e0e6f0" }}>
              <span>Total Cost</span>
              <span>£{costResult.totalCost.toFixed(2)}</span>
            </div>
            <div style={{ fontSize: 11, color: "#8a95a3", marginTop: 6 }}>
              {supplier.paymentTerms ? `Payment: ${supplier.paymentTerms} — this authorises payment, not just dispatch.` : "This authorises payment, not just dispatch."}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <AdminBccToggles value={bcc} onChange={setBcc} disabled={approving} />
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={onClose} disabled={approving} style={{ flex: 1, padding: "11px", borderRadius: 6, border: "1px solid #d0d8e8", background: "#fff", color: "#666", fontSize: 13, cursor: approving ? "default" : "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button
            onClick={handleApprove}
            disabled={approving}
            style={{ flex: 2, padding: "11px", borderRadius: 6, border: "none", background: approving ? "#8fa3c4" : "#3a52cc", color: "#fff", fontSize: 13, fontWeight: 700, cursor: approving ? "default" : "pointer", fontFamily: "inherit" }}
          >
            {approving ? "Sending…" : "✓ Approve & Send PO"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Job calendar strip ──────────────────────────────────────────────────────
// Accepted/Contracted jobs laid out by day (dates from getJobDates - job
// start/end, else the calculator's Scheduled Date). A multi-day job shows
// on every day it covers. Jobs with no date at all sit behind the
// "Unscheduled (N)" chip instead. Dry-ice PO order-by dates show as small
// markers on their day, coloured by PO state. Clicking any pill, marker or
// list row opens that job's detail panel.
const GOOGLE_REVIEW_LINK = "https://g.page/r/CT1oLlgW5TLlEBM/review";
const SCHEDULED_STATUSES = JOB_STATUSES; // Accepted, Deposit pending/paid, Contracted
// A booked job shown filled on the calendar: Contracted, or (Automotive)
// Deposit paid. The line under an unconfirmed one says what's outstanding.
const isConfirmedJob = q => q["Status"] === "Contracted" || q["Status"] === DEPOSIT_PAID;
const jobConfirmLine = q => {
  if (isConfirmedJob(q)) return "";
  if (q["Category"] === "Automotive") return q["Status"] === DEPOSIT_PENDING ? "Deposit pending" : "Deposit invoice not sent";
  return "Not yet contracted";
};

// PO marker colours by poStatus. "pending" = details saved (Requested
// Delivery Date + supplier) but not yet due or sent. Nothing writes
// "confirmed" yet - it's here so the marker is ready when that state exists.
// color = the dot; bg/text = the header chip's tint and label colour.
const PO_MARKER = {
  due:       { color: "#D08A1E", bg: "#FBF0DC", text: "#93631A", label: "PO due" },
  sent:      { color: "#3A52CC", bg: "#E8ECFB", text: "#2B3E9E", label: "PO sent" },
  confirmed: { color: "#5E8A4E", bg: "#E6EFE2", text: "#4A6E3D", label: "PO confirmed" },
  pending:   { color: "#9AA3B5", bg: "#EEF0F4", text: "#5F6878", label: "Order by" },
};

// Dry ice state for a job's ice-crystal icon (from poOrderBy):
// ordered = PO sent or confirmed (filled icon); otherwise an outline whose
// colour is the urgency: grey = not due yet, amber = order due within 3
// days, red = overdue. Only a visual layer - Needs attention, the morning
// email and the panel's Dry ice section are unchanged.
const ICE_COLOURS = { ordered: "#4E7A3F", later: "#9AA3B5", soon: "#D08A1E", overdue: "#C0392B" };
// Every job that uses dry ice gets the crystal. Without saved order details
// (delivery date + supplier) there's no order-by date yet, so it shows as
// grey "not ordered".
function usesDryIce(q) {
  try {
    const cb = JSON.parse(q?.["State JSON"] || "null")?.costBreakdown;
    if (!cb) return true; // no breakdown saved: every job is dry ice blasting
    return (Number(cb.dryIceKgAdjusted) || 0) > 0 || (Number(cb.dryIceCost) || 0) > 0;
  } catch (e) { return true; }
}
function iceInfo(po, q) {
  if (!po) return q && usesDryIce(q) ? { kind: "later", label: "Dry ice not ordered (no order details saved yet)" } : null;
  if (po.state === "sent" || po.state === "confirmed") return { kind: "ordered", label: `Dry ice ordered (order-by ${fmtDay(po.date)})` };
  const days = Math.round((parseDate(po.date) - parseDate(toISODate(new Date()))) / 86400000);
  if (days < 0) return { kind: "overdue", label: `Dry ice order overdue: order-by was ${fmtDay(po.date)}` };
  if (days <= 3) return { kind: "soon", label: `Dry ice to order by ${fmtDay(po.date)}` };
  return { kind: "later", label: `Dry ice to order by ${fmtDay(po.date)}` };
}
// Small ice crystal: filled disc when ordered, outlined (urgency colour)
// when not.
function IceIcon({ ice, size = 14 }) {
  if (!ice) return null;
  const c = ICE_COLOURS[ice.kind];
  const filled = ice.kind === "ordered";
  return (
    <span title={ice.label} aria-label={ice.label} style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: filled ? c : "#fff", border: `1.5px solid ${c}`, boxSizing: "border-box",
    }}>
      <svg width={size - 5} height={size - 5} viewBox="0 0 24 24" fill="none" stroke={filled ? "#fff" : c} strokeWidth="2.6" strokeLinecap="round">
        <path d="M12 2v20M3.3 7l17.4 10M3.3 17L20.7 7M9 4l3 2 3-2M9 20l3-2 3 2" />
      </svg>
    </span>
  );
}

// "SBL-2026-328" -> "328" - enough to tell two same-day markers apart.
const shortQuoteNo = qn => (qn || "").split("-").pop();

const ACCEPTED_OUTLINE = "#AEBBEE";

// Order-by date + marker state for one job, or null if it has no saved PO
// delivery date or the supplier's lead time is unknown.
function poOrderBy(q, suppliers) {
  const po = parsePOData(q);
  if (!po.requestedDeliveryDate) return null;
  const supplier = suppliers.find(s => s.id === po.supplierId);
  if (!supplier) return null;
  const date = toISODate(computeOrderByDate(po.requestedDeliveryDate, supplier.defaultLeadTimeDays));
  if (!date) return null;
  return { q, date, state: PO_MARKER[po.poStatus] ? po.poStatus : "pending" };
}

// Schedule strip zoom levels. colMin keeps each day column readable; when the
// range doesn't fit at that width the strip scrolls sideways. Narrower
// columns get the compact PO marker, smaller bar text/icons and one-letter
// weekdays. "1 month" runs to the same date next month (28-31 days).
const SCHEDULE_ZOOMS = {
  "2w": { label: "2 weeks", colMin: 64, icon: 13, barFont: 11, subFont: 10, compactMarkers: false, weekday: "short" },
  "3w": { label: "3 weeks", colMin: 50, icon: 12, barFont: 10, subFont: 9, compactMarkers: true, weekday: "short" },
  "1m": { label: "1 month", colMin: 40, icon: 11, barFont: 10, subFont: 9, compactMarkers: true, numberOnlyMarkers: true, weekday: "narrow" },
};
const SCHEDULE_ZOOM_KEY = "sublime_schedule_zoom";
function zoomDayCount(zoom, startDate) {
  if (zoom === "3w") return 21;
  if (zoom === "1m") {
    const end = new Date(startDate);
    end.setMonth(end.getMonth() + 1);
    return Math.round((end - startDate) / 86400000);
  }
  return 14;
}

const fmtDay = iso => parseDate(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

// One job as a single bar across its days (Gantt-style). clipStart/clipEnd
// mean the job carries on past the visible range on that side - drawn with
// a flat end and a ‹ / › so it doesn't read as starting/ending there. thin
// = the phone layout: a bar with no label (the Upcoming list carries names).
// ice (iceInfo) adds the job's dry ice crystal in the corner; the
// start of the bar - the header marker only shows on the order-by day,
// which is usually days before the job itself.
function JobPill({ q, title, onOpen, thin, clipStart, clipEnd, ice, scale = SCHEDULE_ZOOMS["2w"] }) {
  const contracted = isConfirmedJob(q);
  const r = thin ? 4 : 6;
  return (
    <button
      onClick={() => onOpen(q)}
      title={title}
      aria-label={title}
      style={{
        position: "relative", display: "block", width: "100%", minWidth: 0, height: thin ? 8 : undefined,
        padding: thin ? 0 : scale.compactMarkers ? "3px 5px" : "4px 8px",
        ...(!thin && ice ? { paddingRight: scale.compactMarkers ? 18 : 22 } : {}),
        borderRadius: `${clipStart ? 0 : r}px ${clipEnd ? 0 : r}px ${clipEnd ? 0 : r}px ${clipStart ? 0 : r}px`,
        border: `1px solid ${contracted ? C.brand : ACCEPTED_OUTLINE}`,
        background: contracted ? C.brand : C.surface,
        color: contracted ? "#fff" : C.brand,
        textAlign: "left", cursor: "pointer", lineHeight: 1.25,
      }}
    >
      {!thin && (
        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
          {clipStart && <span style={{ fontSize: 11, opacity: 0.7, flexShrink: 0 }}>‹</span>}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: scale.barFont, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {q["Client Name"] || "—"}
            </div>
            {/* Sector icon in the bar's own colour, so bar colours still mean
                Contracted / Accepted. */}
            <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: scale.subFont, opacity: 0.75, minWidth: 0 }}>
              {q["Category"] && <SectorIcon icon={getSector(q["Category"]).icon} size={scale.icon} />}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q["Quote Number"]}</span>
            </div>
            {!contracted && (
              <div style={{ fontSize: Math.max(9, scale.subFont - 1), color: "#8a95a3", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{jobConfirmLine(q)}</div>
            )}
            {q["Status"] === DEPOSIT_PAID && (
              <div style={{ fontSize: Math.max(9, scale.subFont - 1), fontWeight: 600, opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Deposit paid</div>
            )}
          </div>
          {clipEnd && <span style={{ fontSize: 11, opacity: 0.7, flexShrink: 0 }}>›</span>}
        </div>
      )}
      {/* Dry ice state in the top-right corner. */}
      {!thin && ice && <span style={{ position: "absolute", top: 3, right: 3 }}><IceIcon ice={ice} size={scale.compactMarkers ? 12 : 14} /></span>}
    </button>
  );
}


function JobCalendarStrip({ quotes, suppliers, isMobile, onOpen }) {
  // Zoom is desktop-only (the phone layout stays a one-week strip plus the
  // Upcoming list) and remembered per browser.
  const [zoom, setZoomState] = useState(() => {
    try { const z = window.localStorage.getItem(SCHEDULE_ZOOM_KEY); return SCHEDULE_ZOOMS[z] ? z : "2w"; } catch (e) { return "2w"; }
  });
  const setZoom = z => {
    setZoomState(z);
    try { window.localStorage.setItem(SCHEDULE_ZOOM_KEY, z); } catch (e) { /* storage blocked: still works this visit */ }
  };
  const scale = SCHEDULE_ZOOMS[isMobile ? "2w" : zoom];
  // Fold the whole strip away (same as the other dashboard sections).
  const [open, setOpen] = useState(() => { try { return window.localStorage.getItem("sublime_dash_open_schedule") !== "0"; } catch (e) { return true; } });
  const toggleOpen = () => setOpen(o => { try { window.localStorage.setItem("sublime_dash_open_schedule", o ? "0" : "1"); } catch (e) { /* storage blocked */ } return !o; });
  const [offset, setOffset] = useState(0); // in days, from today
  const rangeStart = parseDate(new Date());
  rangeStart.setDate(rangeStart.getDate() + offset);
  const dayCount = isMobile ? 7 : zoomDayCount(zoom, rangeStart);
  // Page by one whole range. Month view moves by calendar month (so back
  // from 2 Nov is 2 Oct, not 31 days), the others by their fixed length.
  const page = dir => {
    if (isMobile || zoom !== "1m") { setOffset(o => o + dir * dayCount); return; }
    const target = new Date(rangeStart);
    target.setMonth(target.getMonth() + dir);
    setOffset(o => o + Math.round((target - rangeStart) / 86400000));
  };
  const [showUnscheduled, setShowUnscheduled] = useState(false);

  const jobs = quotes.filter(q => q["Is Current"] !== "No" && SCHEDULED_STATUSES.includes(q["Status"]));
  const dated = [];
  const unscheduled = [];
  jobs.forEach(q => {
    const { start, end } = getJobDates(q);
    if (start) dated.push({ q, start, end });
    else unscheduled.push(q);
  });
  const markers = jobs.map(q => poOrderBy(q, suppliers)).filter(Boolean);

  const today = toISODate(new Date());
  const days = Array.from({ length: dayCount }, (_, i) => {
    const d = parseDate(new Date());
    d.setDate(d.getDate() + offset + i);
    return d;
  });
  const first = toISODate(days[0]);
  const last = toISODate(days[dayCount - 1]);
  const rangeLabel = `${days[0].toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${days[dayCount - 1].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  const inRange = dated.filter(j => j.end >= first && j.start <= last);

  // Mobile-only "Upcoming" list - always from today, regardless of paging.
  // Ongoing jobs count as upcoming (listed at today's date).
  // Jobs only; a job's dry ice state is its dot.
  const upcoming = dated.filter(j => j.end >= today)
    .map(j => ({ date: j.start < today ? today : j.start, po: markers.find(m => m.q === j.q), ...j }))
    .sort((a, b) => a.date.localeCompare(b.date)).slice(0, 5);

  const navBtn = { padding: "4px 10px", borderRadius: 8, border: "none", background: "#F1F3F9", color: C.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer" };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: isMobile ? 16 : 24, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: open ? 14 : 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <button onClick={toggleOpen} aria-expanded={open} style={{ display: "flex", alignItems: "center", gap: 8, border: "none", background: "none", padding: 0, cursor: "pointer", fontSize: 13, fontWeight: 700, color: C.text }}>
            <span style={{ color: C.textFaint, fontSize: 11, width: 10 }}>{open ? "▾" : "▸"}</span>Schedule
          </button>
          <div style={{ fontSize: 12, color: C.textMuted }}>{rangeLabel}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {unscheduled.length > 0 && (
            <button
              onClick={() => setShowUnscheduled(v => !v)}
              style={{ ...navBtn, borderRadius: 999, background: TONE.amber.bg, color: TONE.amber.text }}
            >
              Unscheduled ({unscheduled.length})
            </button>
          )}
          {!isMobile && (
            <div role="group" aria-label="Zoom" style={{ display: "inline-flex", background: "#F1F3F9", borderRadius: 8, padding: 2, marginRight: 4 }}>
              {Object.entries(SCHEDULE_ZOOMS).map(([k, z]) => (
                <button
                  key={k}
                  onClick={() => setZoom(k)}
                  aria-pressed={zoom === k}
                  style={{
                    padding: "3px 9px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer",
                    background: zoom === k ? C.surface : "transparent", color: zoom === k ? C.brand : C.textMuted,
                    boxShadow: zoom === k ? "0 1px 2px rgba(20,30,70,0.12)" : "none",
                  }}
                >
                  {z.label}
                </button>
              ))}
            </div>
          )}
          <button style={navBtn} onClick={() => page(-1)} aria-label="Previous">‹</button>
          <button style={navBtn} onClick={() => setOffset(0)}>Today</button>
          <button style={navBtn} onClick={() => page(1)} aria-label="Next">›</button>
        </div>
      </div>

      {open && (<>
      {showUnscheduled && unscheduled.length > 0 && (
        <div style={{ background: "#F8F9FB", borderRadius: 12, marginBottom: 14, padding: 4 }}>
          {unscheduled.map(q => (
            <button
              key={q["Quote Number"]}
              onClick={() => onOpen(q)}
              style={{
                display: "flex", justifyContent: "space-between", gap: 12, width: "100%", padding: "8px 10px",
                border: "none", borderRadius: 8, background: "transparent",
                textAlign: "left", cursor: "pointer", fontSize: 12, color: C.text,
              }}
            >
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {q["Client Name"] || "—"} <span style={{ color: C.textMuted }}>· {q["Quote Number"]}</span>
              </span>
              <span style={{ color: C.textMuted, flexShrink: 0 }}>{q["Status"]}</span>
            </button>
          ))}
        </div>
      )}

      {/* One grid for the whole range: row 1 = day headers (+ PO markers),
          then one row per lane of job bars. Each job is a single element
          spanning its day columns, packed into the first lane it fits
          without overlapping. Weekend shading sits behind everything as
          full-height cells. */}
      {(() => {
        const dayIsos = days.map(toISODate);
        const bars = inRange
          .map(j => ({ ...j, s: j.start < first ? first : j.start, e: j.end > last ? last : j.end }))
          .sort((a, b) => a.s.localeCompare(b.s) || b.e.localeCompare(a.e));
        const laneEnds = [];
        bars.forEach(b => {
          let lane = laneEnds.findIndex(e => e < b.s);
          if (lane === -1) { lane = laneEnds.length; laneEnds.push(b.e); } else laneEnds[lane] = b.e;
          b.lane = lane;
        });
        const laneCount = Math.max(1, laneEnds.length);
        return (
          <div style={{ overflowX: isMobile ? "visible" : "auto", paddingBottom: isMobile ? 0 : 2 }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? `repeat(${dayCount}, minmax(0, 1fr))` : `repeat(${dayCount}, minmax(${scale.colMin}px, 1fr))`,
            gridTemplateRows: `auto repeat(${laneCount}, auto) 6px`,
            columnGap: 4, rowGap: 3,
          }}>
            {days.map((d, i) => (d.getDay() === 0 || d.getDay() === 6) && (
              <div key={`bg-${dayIsos[i]}`} style={{ gridColumn: i + 1, gridRow: "1 / -1", background: "#F8F9FB", borderRadius: 8 }} />
            ))}

            {days.map((d, i) => {
              const iso = dayIsos[i];
              const isToday = iso === today;
              return (
                <div key={iso} style={{ gridColumn: i + 1, gridRow: 1, minWidth: 0, textAlign: "center", padding: "4px 2px 2px" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.textFaint, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {d.toLocaleDateString("en-GB", { weekday: isMobile ? "narrow" : scale.weekday })}
                  </div>
                  <div style={{
                    display: "inline-block", minWidth: 22, marginTop: 2, padding: "1px 4px", borderRadius: 999,
                    fontSize: 12, fontWeight: 700,
                    background: isToday ? C.brand : "transparent", color: isToday ? "#fff" : C.text,
                  }}>
                    {d.getDate()}
                  </div>
                </div>
              );
            })}

            {bars.length === 0 && <div style={{ gridColumn: "1 / -1", gridRow: 2, height: isMobile ? 12 : 30 }} />}

            {bars.map(b => {
              const col = dayIsos.indexOf(b.s) + 1;
              const span = dayIsos.indexOf(b.e) - col + 2;
              const { q, start, end } = b;
              const po = markers.find(m => m.q === q);
              const ice = iceInfo(po, q);
              const poNote = ice ? ` · ${ice.label}` : "";
              return (
                <div key={q["Quote Number"]} style={{ gridColumn: `${col} / span ${span}`, gridRow: b.lane + 2, minWidth: 0, padding: "0 2px" }}>
                  <JobPill
                    q={q}
                    onOpen={onOpen}
                    thin={isMobile}
                    clipStart={start < first}
                    clipEnd={end > last}
                    ice={ice}
                    scale={scale}
                    title={`${q["Client Name"] || "No client"} · ${q["Quote Number"]}${q["Category"] ? ` · ${getSector(q["Category"]).label}` : ""} · ${q["Status"] === "Contracted" ? "Contracted" : "Accepted, not yet contracted"} · ${start === end ? fmtDay(start) : `${fmtDay(start)} – ${fmtDay(end)}`}${poNote}`}
                  />
                </div>
              );
            })}
          </div>
          </div>
        );
      })()}

      {inRange.length === 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: C.textFaint, textAlign: "center" }}>
          No booked jobs in this range.
        </div>
      )}

      {isMobile && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Upcoming</div>
          {upcoming.length === 0 && <div style={{ fontSize: 12, color: C.textFaint }}>Nothing scheduled.</div>}
          {upcoming.map(u => (
            <button
              key={u.q["Quote Number"]}
              onClick={() => onOpen(u.q)}
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "8px 0", border: "none", borderTop: `1px solid ${C.border}`, background: "transparent", textAlign: "left", cursor: "pointer" }}
            >
              <div style={{ width: 70, flexShrink: 0, fontSize: 11, fontWeight: 600, color: C.textMuted }}>{fmtDay(u.date)}</div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {u.q["Client Name"] || "No client"} <span style={{ color: C.textMuted }}>· {u.q["Quote Number"]}</span>
              </div>
              <IceIcon ice={iceInfo(u.po, u.q)} size={16} />
              <span style={{
                flexShrink: 0, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999,
                border: `1px solid ${isConfirmedJob(u.q) ? C.brand : ACCEPTED_OUTLINE}`,
                background: isConfirmedJob(u.q) ? C.brand : C.surface,
                color: isConfirmedJob(u.q) ? "#fff" : C.brand,
              }}>
                {u.end !== u.start ? `Job · ${Math.round((parseDate(u.end) - parseDate(u.start)) / 86400000) + 1}d` : "Job"}
              </span>
            </button>
          ))}
        </div>
      )}

      </>)}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
// focus: { quoteNumber, sendNote } for a quote the calculator just saved.
// The log opens with that row highlighted and its detail panel showing.
// topContent: shown above everything else (the app's Unsaved drafts strip).
export default function QuoteLog({ onCreateAmendment, onCopyQuote, onOpenReadOnly, focus, onFocusShown, topContent }) {
  const { user } = useUser();
  const changedByLabel = user?.primaryEmailAddress?.emailAddress || user?.fullName || "Unknown";
  // PO approval is gated behind the same admin flag as Company Settings -
  // "Edward or James can approve" is satisfied by both being flagged admin
  // in Clerk, no separate permission concept needed.
  const isAdmin = user?.publicMetadata?.role === "admin";
  const approverName = user?.firstName || user?.fullName || changedByLabel;
  const [isMobile] = useState(() => window.innerWidth < 768);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterCategory, setFilterCategory] = useState("All");
  const [view, setView] = useState("active"); // "active" | "completed" | "closed"
  // Default: newest first by last updated. Any header toggles the sort; the
  // choice is remembered in this browser.
  const [sortKey, setSortKey] = useState(() => loadSort().key);
  const [sortDir, setSortDir] = useState(() => loadSort().dir);
  useEffect(() => {
    try { window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ key: sortKey, dir: sortDir })); } catch (e) { /* storage blocked */ }
  }, [sortKey, sortDir]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [sendingEmail, setSendingEmail] = useState({});
  const [confirmSend, setConfirmSend] = useState(null);
  // Per-quote-number: "checking" | "pending" | "none". Populated lazily for
  // whatever's currently showing at Accepted status, via the same
  // findOutstandingDocument check api/contracts.js's send path itself uses
  // for duplicate-send protection - so "already sent" means the same thing
  // here as it does there, not a second/independent notion of it.
  const [contractStatus, setContractStatus] = useState({});
  const contractStatusRef = useRef(contractStatus); // for load(), which is created once
  contractStatusRef.current = contractStatus;
  // RAMS on on-site Contracted jobs: "checking" | "pending" | "signed" | "none".
  // Read through ramsOf(), which trusts History first (see below).
  const [ramsStatus, setRamsStatus] = useState({});
  // Who each pending GoodSign document went to, per quote: { contract, rams }
  // (from the status checks), for the "no longer matches" warning.
  const [pendingTo, setPendingTo] = useState({});
  const [voiding, setVoiding] = useState(false);
  const ramsStatusRef = useRef(ramsStatus);
  ramsStatusRef.current = ramsStatus;
  const [suppliers, setSuppliers] = useState([]);
  const [sendLog, setSendLog] = useState([]);
  // Jobs with findings the client hasn't been told yet ({ base: count }),
  // from the Job Notes (JobNotes.gs). Loaded once, and again when a note
  // changes; a failure just leaves Needs attention without them.
  const [sendingReview, setSendingReview] = useState(false);
  const [jobFlags, setJobFlags] = useState({});
  const loadJobFlags = React.useCallback(async () => {
    try {
      const res = await apiFetch("/api/sheets-proxy", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "jobNoteFlags" }) });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success !== false && data.flags) setJobFlags(data.flags);
    } catch (e) { /* not set up yet, or offline */ }
  }, []);
  useEffect(() => { loadJobFlags(); }, [loadJobFlags]);
  const [statusLog, setStatusLog] = useState([]); // StatusChangeLog, for History
  // Local edits to the open quote's PO fields - a separate draft object
  // rather than mutating selectedQuote directly, so opening the panel
  // doesn't look "saved" until Save PO Details is actually clicked.
  const [poDraft, setPoDraft] = useState(null);
  const [savingPO, setSavingPO] = useState(false);
  const [confirmingPO, setConfirmingPO] = useState(false);
  // Same draft-then-save pattern as poDraft, for the open quote's job dates.
  const [jobDatesDraft, setJobDatesDraft] = useState({ jobStartDate: "", jobEndDate: "" });
  const [savingJobDates, setSavingJobDates] = useState(false);
  const [jobDatesError, setJobDatesError] = useState(null);
  const [showRaisePOModal, setShowRaisePOModal] = useState(false);
  // "Copy discount" on the open quote's Copy as new quote - off each time.
  const [copyDiscount, setCopyDiscount] = useState(false);
  useEffect(() => { setCopyDiscount(false); }, [selectedQuote]);

  // ── Record page (/record/:ref) ──────────────────────────────────────────
  // The full record for one quote, opened from the panel's Open button. It
  // renders here (not as its own page) so it shares this component's data
  // and actions; the open quote is selectedQuote, so going back to the list
  // shows the panel on the same row.
  const location = useLocation();
  const navigate = useNavigate();
  const recordRef = matchPath("/record/:ref", location.pathname)?.params.ref || null;
  const listScrollRef = useRef(null); // list scroll position when Open was clicked
  // After opening the record: "jobStart" focuses Job Start (Set job date),
  // "documents" scrolls to Documents (the old-address warning).
  const [recordScrollTo, setRecordScrollTo] = useState(null);
  const openRecord = (q, scrollTo = null) => {
    listScrollRef.current = window.scrollY;
    setSelectedQuote(q);
    setRecordScrollTo(scrollTo);
    navigate(`/record/${encodeURIComponent(q["Quote Number"])}`, { state: { fromLog: true } });
  };
  // Another version of the same job (History, superseded header link).
  const openOtherRecord = qn => navigate(`/record/${encodeURIComponent(qn)}`, { replace: true, state: location.state });
  // Back to the list: browser Back when we came from it (keeps history
  // tidy), otherwise straight to the log. Either way the panel stays open.
  const backToLog = () => { if (location.state?.fromLog) navigate(-1); else navigate("/"); };
  const recordRow = recordRef ? quotes.find(q => q["Quote Number"] === recordRef) || null : null;
  useEffect(() => {
    if (recordRow && selectedQuote?.["Quote Number"] !== recordRef) setSelectedQuote(recordRow);
  }, [recordRow, recordRef]); // eslint-disable-line react-hooks/exhaustive-deps
  // Top of the page on the record; back on the list, where it was (or the
  // row, after landing on a record link directly).
  const wasRecord = useRef(!!recordRef);
  React.useLayoutEffect(() => {
    if (recordRef) window.scrollTo(0, 0);
    else if (wasRecord.current) {
      if (listScrollRef.current != null) window.scrollTo(0, listScrollRef.current);
      else if (selectedQuote) document.getElementById(`quote-row-${selectedQuote["Quote Number"]}`)?.scrollIntoView({ block: "center" });
    }
    wasRecord.current = !!recordRef;
  }, [recordRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // silent: a background refresh (tab regained focus, or the timer). The
  // list stays as it is while it loads, and a failure keeps the data shown.
  const [lastLoaded, setLastLoaded] = useState(null);
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) { setLoading(true); setError(null); }
    try {
      const [data] = await Promise.all([
        fetchAllQuotes(),
        fetchSuppliers().then(setSuppliers).catch(() => {}), // suppliers are secondary - a failed fetch shouldn't block the quote list loading
        fetchSendLog().then(setSendLog).catch(() => {}), // same - only feeds "Last sent to" and History
        fetchStatusLog().then(setStatusLog).catch(() => {}), // History only
      ]);
      setQuotes(data);
      setLastLoaded(Date.now());
      if (silent) setError(null);
      // Re-check contract status on every refresh (↻ and the quiet one), so a
      // contract voided or signed in GoodSign shows without a page reload.
      // Quotes not checked yet are picked up by the effect further down.
      const known = contractStatusRef.current;
      data.filter(q => q["Status"] === "Accepted" && q["Quote Number"] in known && known[q["Quote Number"]] !== "checking")
        .forEach(q => checkContractStatus(q["Quote Number"]));
      const knownRams = ramsStatusRef.current;
      data.filter(q => needsRamsCheck(q) && q["Quote Number"] in knownRams && knownRams[q["Quote Number"]] !== "checking")
        .forEach(q => checkRamsStatus(q));
    } catch (e) {
      if (!silent) setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the log current without a manual refresh: when the tab or app comes
  // back into view (if the data is over 30s old), and every 2 minutes while
  // it's visible.
  const lastLoadedRef = useRef(null);
  lastLoadedRef.current = lastLoaded;
  useEffect(() => {
    const stale = ms => !lastLoadedRef.current || Date.now() - lastLoadedRef.current > ms;
    const onBack = () => { if (document.visibilityState === "visible" && stale(30000)) load({ silent: true }); };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    const timer = setInterval(() => { if (document.visibilityState === "visible") load({ silent: true }); }, 120000);
    return () => { document.removeEventListener("visibilitychange", onBack); window.removeEventListener("focus", onBack); clearInterval(timer); };
  }, [load]);
  // Re-render the "Updated … ago" label every 30s.
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick(n => n + 1), 30000); return () => clearInterval(id); }, []);
  const updatedAgo = !lastLoaded ? "" : (() => {
    const mins = Math.floor((Date.now() - lastLoaded) / 60000);
    return mins < 1 ? "Updated just now" : `Updated ${mins} min ago`;
  })();

  // The quote just saved: { quoteNumber, sendNote }. Highlights its row and
  // labels the detail panel until another quote is opened.
  const [justSaved, setJustSaved] = useState(null);
  const focusRetried = useRef(false);
  useEffect(() => {
    if (!focus?.quoteNumber || loading) return;
    // Back from View Quote: just reopen the panel, no "saved" banner.
    if (focus.reopen) {
      const row = quotes.find(q => q["Quote Number"] === focus.quoteNumber);
      if (row) setSelectedQuote(row);
      if (onFocusShown) onFocusShown();
      return;
    }
    const row = quotes.find(q => q["Quote Number"] === focus.quoteNumber);
    if (!row) {
      // A row written a moment ago can lag the read; try once more.
      if (!focusRetried.current) { focusRetried.current = true; setTimeout(load, 1500); }
      return;
    }
    setSearch(""); setFilterStatus("All"); setFilterCategory("All");
    setView(tabOfStatus(row["Status"]));
    setJustSaved({ quoteNumber: focus.quoteNumber, sendNote: focus.sendNote || "", unchanged: !!focus.unchanged });
    setSelectedQuote(row);
    if (onFocusShown) onFocusShown();
    setTimeout(() => document.getElementById(`quote-row-${focus.quoteNumber}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [focus, loading, quotes, load, onFocusShown]);
  const isJustSaved = q => justSaved?.quoteNumber === q["Quote Number"];
  // Light green: the save worked (was a yellow that read as a warning).
  const JUST_SAVED_BG = "#E8F7EE";

  // Resets whenever a different quote is opened (or the panel closes) -
  // defaults Requested Delivery Date from the job start date (jobStartDate,
  // else the calculator's Scheduled Date - see getJobDates) when one was
  // set, and Supplier to the Primary dry-ice supplier (else the first active
  // one), both left blank/editable if nothing sensible to default to.
  useEffect(() => {
    if (!selectedQuote) { setPoDraft(null); return; }
    const existing = parsePOData(selectedQuote);
    const activeSuppliers = suppliers.filter(s => s.active);
    setPoDraft({
      supplierId: existing.supplierId || primaryDryIceSupplier(findDryIceSuppliers(suppliers))?.id || activeSuppliers[0]?.id || "",
      deliveryDestination: existing.deliveryDestination || "Yard",
      requestedDeliveryDate: existing.requestedDeliveryDate || getJobDates(selectedQuote).start,
    });
  }, [selectedQuote, suppliers]);

  // Edit dates starts from what the record shows: Job Dates (column X), else
  // the job date saved with the quote (State JSON jobDate, e.g. Rob's 16 Nov).
  // (getJobDates: blank once Edit dates has cleared the Scheduled Date).
  const jobDatesStart = q => ({ jobStartDate: getJobDates(q).start || "", jobEndDate: toISODate(parsePOData(q).jobEndDate) || "" });
  useEffect(() => {
    setJobDatesDraft(jobDatesStart(selectedQuote));
    setJobDatesError(null);
  }, [selectedQuote]); // eslint-disable-line react-hooks/exhaustive-deps

  // Writes column X only (see saveJobDates) - never a row update, amendment
  // or status change, so the quote's Status and Date Updated stay as they
  // were.
  // "AUT-2026-044 · Rob Moss · 16 Nov–17 Nov · Deposit paid"
  const clashLine = q => { const d = getJobDates(q); return `${q["Quote Number"]} · ${q["Client Name"] || "No client"} · ${fmtDay(d.start)}${d.end !== d.start ? `–${fmtDay(d.end)}` : ""} · ${q["Status"]}`; };
  async function handleSaveJobDates() {
    if (!selectedQuote?._rowIndex) return;
    // The box starts filled with the effective date, so blank = cleared
    // (unscheduled), never "fall back to the quote's Scheduled Date".
    const start = jobDatesDraft.jobStartDate;
    if (jobDatesDraft.jobEndDate && !start) { setJobDatesError("Set a start date before an end date."); return; }
    if (jobDatesDraft.jobEndDate && jobDatesDraft.jobEndDate < start) { setJobDatesError("End date can't be before the start date."); return; }
    // Another booked job (Accepted or later) on any of these days: warn first.
    const clashes = overlappingJobs({ quoteNumber: selectedQuote["Quote Number"], start, end: jobDatesDraft.jobEndDate, test: isTestQuote(selectedQuote) }, quotes);
    if (clashes.length && !window.confirm(`These dates overlap ${clashes.length === 1 ? "another booked job" : `${clashes.length} booked jobs`}:\n\n${clashes.map(clashLine).join("\n")}\n\nSave the dates anyway?`)) return;
    setJobDatesError(null);
    setSavingJobDates(true);
    try {
      const poData = await saveJobDates(selectedQuote, jobDatesDraft);
      const poDataStr = JSON.stringify(poData);
      setQuotes(qs => qs.map(q => q["Quote Number"] === selectedQuote["Quote Number"] ? { ...q, "PO Data": poDataStr } : q));
      setSelectedQuote(prev => (prev ? { ...prev, "PO Data": poDataStr } : prev));
      setSuccessMsg("Job dates saved");
      setTimeout(() => setSuccessMsg(""), 3000);
      setEditingDates(false);
    } catch (e) {
      setJobDatesError("Failed to save job dates: " + e.message);
    } finally {
      setSavingJobDates(false);
    }
  }

  // Column X only, merged (see markPOConfirmed) - no status change.
  async function handleMarkPOConfirmed() {
    if (!selectedQuote?._rowIndex) return;
    if (!window.confirm(`Mark the dry-ice PO for ${selectedQuote["Quote Number"]} as confirmed by the supplier?`)) return;
    setConfirmingPO(true);
    try {
      const poData = await markPOConfirmed(selectedQuote, changedByLabel);
      const poDataStr = JSON.stringify(poData);
      setQuotes(qs => qs.map(q => q["Quote Number"] === selectedQuote["Quote Number"] ? { ...q, "PO Data": poDataStr } : q));
      setSelectedQuote(prev => (prev ? { ...prev, "PO Data": poDataStr } : prev));
      setSuccessMsg("PO marked as confirmed");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) {
      alert("Failed to mark PO confirmed: " + e.message);
    } finally {
      setConfirmingPO(false);
    }
  }

  async function handleSavePODetails() {
    if (!selectedQuote?._rowIndex || !poDraft) return;
    setSavingPO(true);
    try {
      const poData = withJobDates(parsePOData(selectedQuote), poDraft);
      await savePODetails(selectedQuote._rowIndex, poData);
      const poDataStr = JSON.stringify(poData);
      setQuotes(qs => qs.map(q => q["Quote Number"] === selectedQuote["Quote Number"] ? { ...q, "PO Data": poDataStr } : q));
      setSelectedQuote(prev => (prev ? { ...prev, "PO Data": poDataStr } : prev));
      setSuccessMsg("Purchase order details saved");
      setTimeout(() => setSuccessMsg(""), 3000);
      setEditingPO(false);
    } catch (e) {
      alert("Failed to save PO details: " + e.message);
    } finally {
      setSavingPO(false);
    }
  }

  // ── Filtering & sorting ──────────────────────────────────────────────────
  // Superseded rows (Is Current === "No") never appear as their own entry in
  // the main list or participate in tab-bucketing/status-priority sort - a
  // stale row sitting at "Accepted" would otherwise look exactly as
  // actionable as the current version. They're surfaced instead via the
  // "earlier version" disclosure on their current row, below.
  // Test quotes are hidden unless "Show test quotes" is on (remembered in
  // this browser); the tab counts follow the same rule.
  const [showTests, setShowTests] = useState(() => { try { return window.localStorage.getItem("sublime_show_test_quotes") === "1"; } catch (e) { return false; } });
  const toggleShowTests = v => { setShowTests(v); try { window.localStorage.setItem("sublime_show_test_quotes", v ? "1" : "0"); } catch (e) { /* storage blocked */ } };
  const listed = q => q["Is Current"] !== "No" && (showTests || !isTestQuote(q));
  const viewQuotes = quotes.filter(q =>
    listed(q) &&
    statusesOfView(view).includes(q["Status"])
  );
  // An amendment group (027, 027a, 027b) sorts by its latest version: for
  // dates, the newest date across all its versions.
  const groupDate = (q, key) => {
    const base = baseNumber(q["Quote Number"]);
    return quotes.reduce((m, x) => baseNumber(x["Quote Number"]) === base ? Math.max(m, dateValue(x[key])) : m, dateValue(q[key]));
  };
  // Tie-break for rows changed on the same day: the time of the last change
  // ("Updated At", column AB), else the quote's latest History entry (older
  // rows have no Updated At), else the row written last.
  const latestHistory = React.useMemo(() => {
    const m = {};
    [...statusLog, ...sendLog].forEach(e => { if (e.quoteNumber && (!m[e.quoteNumber] || e.timestamp > m[e.quoteNumber])) m[e.quoteNumber] = e.timestamp; });
    return m;
  }, [statusLog, sendLog]);
  const lastChanged = q => (q["Updated At"] ? normaliseTimestamp(q["Updated At"]) : "") || latestHistory[q["Quote Number"]] || "";
  const compareBy = (a, b, key) => {
    if (key.startsWith("Date ")) return groupDate(a, key) - groupDate(b, key);
    if (key.includes("Total")) return (parseFloat(String(a[key] || "").replace(/[£,]/g, "")) || 0) - (parseFloat(String(b[key] || "").replace(/[£,]/g, "")) || 0);
    // Status in workflow order (Draft → Complete), not alphabetical.
    if (key === "Status") return PIPELINE_STATUSES.indexOf(a["Status"] || "Draft") - PIPELINE_STATUSES.indexOf(b["Status"] || "Draft");
    return String(a[key] || "").localeCompare(String(b[key] || ""), "en-GB", { numeric: true });
  };
  const filtered = viewQuotes
    .filter(q => {
      const s = search.toLowerCase();
      if (s && ![
        q["Quote Number"], q["Client Name"], q["Application"],
        q["Site Address"], q["Category"],
      ].some(v => v?.toLowerCase().includes(s))) return false;
      if (filterStatus !== "All" && q["Status"] !== filterStatus) return false;
      if (filterCategory !== "All" && q["Category"] !== filterCategory) return false;
      return true;
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const cmp = compareBy(a, b, sortKey);
      if (cmp) return cmp * dir;
      // Same day / same value: most recently changed first (see lastChanged),
      // then the row written last.
      return (groupDate(b, "Date Updated") - groupDate(a, "Date Updated"))
        || lastChanged(b).localeCompare(lastChanged(a))
        || ((b._rowIndex || 0) - (a._rowIndex || 0));
    });

  // ── Status update ────────────────────────────────────────────────────────
  // Manual, dropdown-driven changes only - every transition here requires a
  // reason (window.prompt, fine for an admin-only action) and is written to
  // the StatusChangeLog audit trail. Fully-automatic transitions (Draft/
  // Amended → Sent via handleSendToClient, Accepted → Contracted via the
  // GoodSign webhook) update the row directly and never call this.
  // Lost asks for a reason (LostDialog). Automotive's deposit steps, and
  // Deposit paid → Complete, take an optional note (StatusNoteDialog). Every
  // other manual change still needs a typed reason.
  const OPTIONAL_NOTE_CHANGE = (from, to) => to === DEPOSIT_PENDING || to === DEPOSIT_PAID || (from === DEPOSIT_PAID && to === "Complete");
  const [statusDialog, setStatusDialog] = useState(null); // { quote, newStatus, kind: "lost" | "note" }
  function handleStatusChange(quote, newStatus) {
    if (!quote._rowIndex) return;
    if (newStatus === quote["Status"]) return;
    if (quote["Is Current"] === "No") {
      alert(`This quote has been superseded by ${quote["Superseded By"] || "a newer revision"} — status changes must be made there.`);
      return;
    }
    if (!manualTransitions(quote).includes(newStatus)) {
      alert(`${quote["Status"]} → ${newStatus} isn't a valid manual status change.`);
      return;
    }
    if (newStatus === LOST) { setStatusDialog({ quote, newStatus, kind: "lost" }); return; }
    if (OPTIONAL_NOTE_CHANGE(quote["Status"], newStatus)) { setStatusDialog({ quote, newStatus, kind: "note" }); return; }
    const reason = window.prompt(`Reason for changing ${quote["Quote Number"]} from ${quote["Status"]} to ${newStatus}:`);
    if (reason === null) return; // cancelled
    if (!reason.trim()) { alert("A reason is required for a manual status change."); return; }
    applyStatusChange(quote, newStatus, { reason: reason.trim() });
  }

  // Writes the change, its History line, and updates the list in place.
  // lost: { reason, note } for a Lost change (Loss Reason / Loss Note columns).
  async function applyStatusChange(quote, newStatus, { reason = "", lost = null } = {}) {
    setUpdatingStatus(true);
    const historyReason = lost ? [lost.reason, lost.note].filter(Boolean).join(", ") : reason;
    try {
      let patch;
      if (lost) {
        patch = await markQuoteLost({ quote, reason: lost.reason, note: lost.note, changedBy: changedByLabel, historyReason });
      } else {
        await updateQuoteStatus(quote._rowIndex, newStatus);
        try {
          await logStatusChange({ quoteNumber: quote["Quote Number"], changedBy: changedByLabel, fromStatus: quote["Status"], toStatus: newStatus, reason: historyReason });
        } catch (e) { console.error("Failed to write StatusChangeLog entry:", e); }
        patch = { Status: newStatus, "Date Updated": new Date().toLocaleDateString("en-GB") };
      }
      // History (and the record's latest note) shows it straight away.
      setStatusLog(l => [...l, { quoteNumber: quote["Quote Number"], timestamp: new Date().toISOString(), changedBy: changedByLabel, fromStatus: quote["Status"], toStatus: newStatus, reason: historyReason }]);
      setQuotes(qs => qs.map(q => q["Quote Number"] === quote["Quote Number"] ? { ...q, ...patch } : q));
      if (selectedQuote?.["Quote Number"] === quote["Quote Number"]) setSelectedQuote(prev => ({ ...prev, ...patch }));
      setSuccessMsg(newStatus === LOST ? `${quote["Quote Number"]} marked lost (${lost.reason})` : `Status updated to ${newStatus}`);
      setTimeout(() => setSuccessMsg(""), 3000);
      setStatusDialog(null);
    } catch (e) {
      alert("Failed to update status: " + e.message);
    } finally {
      setUpdatingStatus(false);
    }
  }

  // ── Convert Rejected to Lost (one-off) ──────────────────────────────────
  // Every row still marked Rejected (any version, tests included) becomes
  // Lost with "Reason not recorded" and a History line. Shown as a list
  // first (ConvertRejectedDialog); written one row at a time, so a failure
  // stops there and the rest can be retried.
  const [convertRejected, setConvertRejected] = useState(null); // { rows, done: [qn], busy }
  const rejectedRows = quotes.filter(q => q["Status"] === "Rejected");
  async function runConvertRejected() {
    const { rows } = convertRejected;
    setConvertRejected(c => ({ ...c, busy: true }));
    const done = [...convertRejected.done];
    for (const q of rows) {
      if (done.includes(q["Quote Number"])) continue;
      try {
        const patch = await markQuoteLost({ quote: q, reason: REASON_NOT_RECORDED, changedBy: changedByLabel, historyReason: "Rejected renamed Lost (reason not recorded)" });
        setQuotes(qs => qs.map(x => x["Quote Number"] === q["Quote Number"] ? { ...x, ...patch } : x));
        setStatusLog(l => [...l, { quoteNumber: q["Quote Number"], timestamp: new Date().toISOString(), changedBy: changedByLabel, fromStatus: "Rejected", toStatus: LOST, reason: "Rejected renamed Lost (reason not recorded)" }]);
        done.push(q["Quote Number"]);
        setConvertRejected(c => ({ ...c, done: [...done] }));
      } catch (e) {
        alert(`Couldn't convert ${q["Quote Number"]}: ${e.message}. The ones already done stay done; try again for the rest.`);
        break;
      }
    }
    setConvertRejected(c => c && ({ ...c, busy: false }));
  }

  // ── Amendment ────────────────────────────────────────────────────────────
  function handleAmend(quote) {
    // An Accepted quote can be amended, but the client has to accept the new
    // version (every new version saves as Draft until it's sent).
    const accepted = quote["Status"] === "Accepted";
    if (accepted && !window.confirm("This quote has been accepted. Amending creates a new version and the client will need to accept it again.")) return false;
    const amendNum = generateAmendmentNumber(quote["Quote Number"], quotes, [...sendLog, ...statusLog]);
    if (onCreateAmendment) {
      const stateJson = quote["State JSON"] ? (() => { try { return JSON.parse(quote["State JSON"]); } catch(e) { return null; } })() : null;
      onCreateAmendment({ originalQuote: quote, amendmentNumber: amendNum, stateJson, startsAsDraft: accepted });
    }
    return true;
  }

  // Draft only: the quote reached the client some other way. No email.
  async function handleMarkAsSent(quote) {
    if (savingContact) return;
    if (!window.confirm(`Mark ${quote["Quote Number"]} as sent? Nothing is emailed.`)) return;
    setUpdatingStatus(true);
    try {
      const date = await markQuoteSentManually({ rowIndex: quote._rowIndex, quoteNumber: quote["Quote Number"], changedBy: changedByLabel });
      const patch = { Status: "Sent", "Date Sent": date, "Date Updated": date };
      setQuotes(qs => qs.map(q => q["Quote Number"] === quote["Quote Number"] ? { ...q, ...patch } : q));
      setSelectedQuote(prev => prev && prev["Quote Number"] === quote["Quote Number"] ? { ...prev, ...patch } : prev);
      setStatusLog(l => [...l, { quoteNumber: quote["Quote Number"], timestamp: new Date().toISOString(), changedBy: changedByLabel, fromStatus: "Draft", toStatus: "Sent", reason: "Marked as sent (not emailed)" }]);
      setSuccessMsg("Marked as sent");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) {
      alert("Couldn't mark as sent: " + e.message);
    } finally {
      setUpdatingStatus(false);
    }
  }

  // Contact details, edited in place on the current version.
  const [contactEdit, setContactEdit] = useState(null); // { name, email, phone, address } while editing
  // Side panel: the More menu (closed again for each quote), and the Job Start
  // field the "Set job date" button takes you to.
  const [moreOpen, setMoreOpen] = useState(false);
  const jobStartRef = useRef(null);
  // Scope Summary in the panel: folded each time a quote is opened.
  const [scopeOpen, setScopeOpen] = useState(false);
  // Job Record: job dates and PO details show as values until Edit.
  const [editingDates, setEditingDates] = useState(false);
  const [editingPO, setEditingPO] = useState(false);
  useEffect(() => { setEditingDates(false); setEditingPO(false); }, [selectedQuote?.["Quote Number"]]);
  useEffect(() => { setMoreOpen(false); setScopeOpen(false); }, [selectedQuote?.["Quote Number"]]);
  const [savingContact, setSavingContact] = useState(false);
  const selectedNumber = selectedQuote?.["Quote Number"];
  useEffect(() => { setContactEdit(null); }, [selectedNumber]);
  function startContactEdit(quote, st) {
    const isAuto = quote["Category"] === "Automotive";
    setContactEdit({
      name: quote["Client Name"] || "",
      email: quote["Client Email"] || "",
      phone: quotePhone(quote),
      address: (isAuto ? st?.client?.address : st?.job?.clientAddress) || "",
    });
  }
  async function saveContactEdit(quote) {
    setSavingContact(true);
    try {
      const updated = await updateContactDetails({ quote, changes: contactEdit, changedBy: changedByLabel });
      setQuotes(qs => qs.map(q => q["Quote Number"] === quote["Quote Number"] ? { ...q, ...updated } : q));
      setSelectedQuote(prev => prev && prev["Quote Number"] === quote["Quote Number"] ? { ...prev, ...updated } : prev);
      fetchStatusLog().then(setStatusLog).catch(() => {});
      setContactEdit(null);
      setSuccessMsg("Contact details saved");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) {
      alert("Couldn't save contact details: " + e.message);
    } finally {
      setSavingContact(false);
    }
  }

  // An earlier version as the starting point for a new amendment. The new
  // amendment still supersedes the LATEST version (so nothing is ever
  // overwritten and there's only one current version); it just starts from
  // this version's details, and records that in its saved state
  // (amendedFrom) for History.
  function handleAmendFromVersion(older) {
    const latest = quotes.find(x => baseNumber(x["Quote Number"]) === baseNumber(older["Quote Number"]) && x["Is Current"] !== "No");
    if (!latest || !onCreateAmendment) return;
    const next = generateAmendmentNumber(latest["Quote Number"], quotes, [...sendLog, ...statusLog]);
    if (!window.confirm(`Start a new amendment from ${older["Quote Number"]} (v${versionOf(older["Quote Number"])})?\n\nNothing is overwritten: the latest version, ${latest["Quote Number"]}, stays as it is until you save. Saving creates ${next}.`)) return;
    let stateJson = null;
    try { stateJson = JSON.parse(older["State JSON"]); } catch (e) { /* no saved state */ }
    onCreateAmendment({ originalQuote: latest, amendmentNumber: next, stateJson, basedOn: older["Quote Number"], sourceQuote: older });
  }

  // History for a quote's whole version group: versions (with price
  // change), sends (incl. contracts) and status changes, oldest first.
  // Version rows only carry a date, so they sort at the start of their day.
  function quoteHistory(q) {
    const base = baseNumber(q["Quote Number"]);
    const versions = quotes.filter(x => baseNumber(x["Quote Number"]) === base)
      .sort((a, b) => versionOf(a["Quote Number"]) - versionOf(b["Quote Number"]));
    const numbers = new Set(versions.map(v => v["Quote Number"]));
    const toNum = v => parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
    const money = n => "£" + n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const dayKey = d => { const t = parseDate(d); return t ? `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")} 00:00:00` : ""; };
    const ev = [];
    versions.forEach((v, i) => {
      let st = null;
      try { st = JSON.parse(v["State JSON"] || "null"); } catch (e) { /* no state */ }
      const vn = versionOf(v["Quote Number"]);
      if (i === 0) {
        ev.push({ t: dayKey(v["Date Created"]), dayOnly: true, text: `Created v${vn} · ${fmt(v["Total (ex VAT)"])} ex VAT`, version: v });
      } else {
        const diff = toNum(v["Total (ex VAT)"]) - toNum(versions[i - 1]["Total (ex VAT)"]);
        const from = st?.amendedFrom ? ` from v${versionOf(st.amendedFrom)}` : "";
        const change = Math.abs(diff) < 0.005 ? "same price" : `${diff > 0 ? "+" : "−"}${money(Math.abs(diff))}`;
        ev.push({ t: dayKey(v["Date Created"]), dayOnly: true, text: `Amended to v${vn}${from} · ${fmt(v["Total (ex VAT)"])} ex VAT (${change})`, version: v });
      }
    });
    sendLog.filter(e => numbers.has(e.quoteNumber)).forEach(e => ev.push({
      t: e.timestamp,
      // Contract sends: "Service Contract" / "RAMS" since Oct 2026; older
      // entries logged both as one "Contract".
      text: e.method === "Contract" ? `Service Contract and RAMS sent${e.to ? ` to ${e.to}` : ""}`
        : (e.method === "Service Contract" || e.method === "RAMS") ? `${e.method} sent${e.to ? ` to ${e.to}` : ""}`
        : isOnFileRow(e) ? `${e.method.replace(/ \(on file\)$/, "")} on file (already out for signature, ID recorded)`
        : `Sent v${versionOf(e.quoteNumber)} to ${e.to}${e.method ? ` (${e.method})` : ""}`,
    }));
    statusLog.filter(e => numbers.has(e.quoteNumber)).forEach(e => ev.push({
      t: e.timestamp,
      // Same status both sides = not a status change (e.g. contact details edited).
      text: e.fromStatus && e.fromStatus === e.toStatus ? e.reason : `Status ${e.fromStatus || "–"} → ${e.toStatus}${e.reason ? `: ${e.reason}` : ""}`,
    }));
    return ev.sort((a, b) => String(a.t).localeCompare(String(b.t)));
  }

  // ── Send to Client ───────────────────────────────────────────────────────
  // Reuses the same email template as the Save & Email flow - it does not
  // recalculate pricing or write a new/duplicate row to the log. Clicking the
  // row button opens a confirmation modal first; this only actually sends
  // once the user reviews/edits the recipient and clicks Confirm. No file
  // attachment is sent - the body links to the full quote page, and a raw
  // .html attachment only risks spam/security filters for no benefit.
  // Opens the confirm with the latest copy of the row (after any contact
  // edit), so it shows the address that's actually on file.
  function openSendConfirm(q) {
    if (savingContact) return;
    setConfirmSend(quotes.find(x => x["Quote Number"] === q["Quote Number"]) || q);
  }
  async function handleSendToClient(quote, { clientName, email, bcc, personalNote, blocks, vehicleMake }) {
    if (!email) { alert("Enter a client email address before sending."); return; }
    if (isTestQuote(quote) && !isInternalOnlySend(email)) { alert(TEST_QUOTE_SEND_BLOCKED); return; }
    const quoteNumber = quote["Quote Number"];
    // Already sent to a client (flagged on the row, or in SendLog): confirm
    // before sending again. Test sends to our own addresses don't ask.
    const sentBefore = quote["Email Sent"] === "Yes" || quote["Status"] === "Sent"
      || sendLog.some(e => e.quoteNumber === quoteNumber && !isOnFileRow(e) && !isInternalOnlySend(e.to));
    if (sentBefore && !isInternalOnlySend(email) && !window.confirm(`Send ${quoteNumber} to ${email} again?`)) return;
    setSendingEmail(s => ({ ...s, [quoteNumber]: true }));
    try {
      await sendQuoteEmail({
        to: email,
        quoteNumber,
        clientName: clientName || "",
        contactName: quoteContactName(quote),
        category: quote["Category"],
        totalIncVat: quote["Total (inc VAT)"] || "—",
        totalExVat: quote["Total (ex VAT)"] || "",
        discount: resolveDiscount(parseStateJson(quote)?.commercial, parseFloat(String(quote["Total (ex VAT)"] || "").replace(/[^0-9.]/g, "")) || 0),
        scopeSummary: quote["Scope Summary"] || "",
        bcc,
        personalNote,
        blocks,
        vehicleMake,
        // Recipient goes to SendLog only (written by the server once the email
        // is accepted) - the quote's client email is never touched by a send.
        log: { method: "Send to Client", sentBy: changedByLabel },
      }).then(r => { if (r?.logEntry?.written) setSendLog(l => [...l, r.logEntry]); });
      // A send to the client (not just to us) marks Email Sent and moves
      // Draft/Amended on to Sent, logged in StatusChangeLog. Never moves a
      // quote that's already further along.
      let after = { emailSent: false, status: quote["Status"] };
      try {
        after = await recordClientSend({
          rowIndex: quote._rowIndex, quoteNumber, currentStatus: quote["Status"], to: email,
          changedBy: changedByLabel, method: "Send to Client", testQuote: isTestQuote(quote),
        });
      } catch (e) { console.error("Failed to mark quote as sent:", e); }
      const patch = { ...(after.emailSent ? { "Email Sent": "Yes", "Date Sent": new Date().toLocaleDateString("en-GB") } : {}), Status: after.status };
      setQuotes(qs => qs.map(q => q["Quote Number"] === quoteNumber ? { ...q, ...patch } : q));
      // selectedQuote is a separate snapshot from `quotes` (captured when
      // "View" was clicked) - the setQuotes call above updates the table but
      // not an already-open detail panel, so it has to be patched too or it
      // keeps showing stale Email Sent/Status values despite the send (and
      // the sheet write) having genuinely succeeded.
      if (selectedQuote?.["Quote Number"] === quoteNumber) {
        setSelectedQuote(prev => ({ ...prev, ...patch }));
      }
      setSuccessMsg(`Quote ${quoteNumber} emailed to ${email}`);
      setTimeout(() => setSuccessMsg(""), 3000);
      setConfirmSend(null);
    } catch (e) {
      alert("Failed to send email: " + e.message);
    } finally {
      setSendingEmail(s => ({ ...s, [quoteNumber]: false }));
    }
  }

  // Contract and RAMS are two independent GoodSign sends — api/contracts.js checks
  // each for an outstanding (already sent, unsigned) document and returns a 409
  // rather than silently creating a duplicate. On 409, confirm with the user and
  // retry — targeting only the blocked document if the other one already went out
  // in the same request (contractAlreadySent), so the retry can't double-send it.
  // alreadySent: documents an earlier call in this same send already got
  // out (e.g. the contract, before a RAMS duplicate warning). The server
  // records every document in the SendLog itself, with its GoodSign ID.
  const SEND_LOG_METHOD = { contract: "Service Contract", rams: "RAMS" };
  async function sendContractRequest(quoteNumber, body, alreadySent = []) {
    try {
      const res = await apiFetch('/api/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteNumber, sentBy: changedByLabel, ...body }),
      });
      const data = await res.json();

      if (res.status === 409 && data.error === 'duplicate_pending') {
        const sentSoFar = [...alreadySent, ...(data.contractAlreadySent ? ['contract'] : [])];
        if (!window.confirm(data.message)) return;
        const nextBody = data.contractAlreadySent
          ? { only: 'rams', force: { rams: true }, expectEmail: body.expectEmail }
          : { force: { ...(body.force || {}), [data.docType]: true }, expectEmail: body.expectEmail };
        return sendContractRequest(quoteNumber, nextBody, sentSoFar);
      }

      if (res.status === 409 && (data.error === 'quote_superseded' || data.error === 'recipient_changed')) {
        alert(data.message);
        return;
      }

      if (data.success) {
        const docs = [...alreadySent, ...(data.sent || [])];
        setSendLog(l => [...l, ...(data.logged || []).filter(e => e.written)]);
        const warn = (data.warnings || []).length ? `\n\n⚠ ${data.warnings.join("\n⚠ ")}` : "";
        const voidLine = (data.voided || []).map(v => `${SEND_LOG_METHOD[v.docType]} to ${v.to} voided.\n`).join("");
        alert(`${voidLine}${docs.length ? docs.map(d => SEND_LOG_METHOD[d]).join(" and ") + " sent to the client." : "Nothing was sent."}${warn}`);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        alert(data.message || `Error: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error sending contract: ${err.message}`);
    }
  }

  // The recipient is read fresh from the sheet (what the send itself will
  // use), not from this page, and the send only goes ahead if it still
  // matches what was confirmed.
  // ramsOnly: on-site Automotive jobs send just the RAMS (no contract).
  async function handleSendContract(quoteNumber, { ramsOnly = false } = {}) {
    if (savingContact) return;
    const testQ = quotes.find(x => x["Quote Number"] === quoteNumber);
    let clientEmail = "";
    try {
      const res = await apiFetch('/api/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteNumber, action: 'recipient' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `error ${res.status}`);
      clientEmail = data.email || "";
    } catch (e) {
      alert(`Couldn't check who the contract would go to, so nothing was sent: ${e.message}`);
      return;
    }
    if (!clientEmail) { alert("No client email on this quote. Add one under Edit contact details before sending the contract."); return; }
    if (isTestQuote(testQ) && !isInternalOnlySend(clientEmail)) { alert(TEST_QUOTE_SEND_BLOCKED); return; }
    const emailLine = `\n\nRecipient: ${clientEmail}`;
    // In-unit jobs get no RAMS (see api/contracts.js).
    const onSite = (() => { try { return (JSON.parse(testQ?.["State JSON"] || "{}").job?.jobLocation || "on-site") !== "in-unit"; } catch (e) { return true; } })();
    const what = ramsOnly ? "the RAMS" : onSite ? "the Service Contract and RAMS" : "the Service Contract (in-unit job, so no RAMS)";
    if (!window.confirm(`Send ${what} to the client for ${quoteNumber}?${emailLine}`)) return;
    await sendContractRequest(quoteNumber, ramsOnly ? { only: "rams", expectEmail: clientEmail } : { expectEmail: clientEmail });
  }

  // Pending documents that went to an address other than the quote's
  // current client email (changed under Edit contact details since):
  // [{ docType, to }], empty when none.
  function stalePending(q) {
    const qn = q["Quote Number"];
    const cur = String(q["Client Email"] || "").trim().toLowerCase();
    const pt = pendingTo[qn];
    if (!cur || !pt || q["Is Current"] === "No") return [];
    const out = [];
    if (q["Status"] === "Accepted" && contractStatus[qn] === "pending" && pt.contract && pt.contract.toLowerCase() !== cur) out.push({ docType: "contract", to: pt.contract });
    if (JOB_STATUSES.includes(q["Status"]) && pt.rams && pt.rams.toLowerCase() !== cur && (q["Status"] === "Accepted" && !isAutomotive(q) ? contractStatus[qn] === "pending" : ramsOf(qn) === "pending")) out.push({ docType: "rams", to: pt.rams });
    return out;
  }
  // Void the stale documents silently and send them again to the current
  // address. The recipient is read fresh from the sheet first, as for Send
  // Contract, and the server checks it again before voiding anything.
  async function handleVoidAndResend(q) {
    if (savingContact || voiding) return;
    const quoteNumber = q["Quote Number"];
    const stale = stalePending(q);
    if (!stale.length) return;
    let newEmail = "";
    try {
      const res = await apiFetch('/api/contracts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ quoteNumber, action: 'recipient' }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `error ${res.status}`);
      newEmail = data.email || "";
    } catch (e) { alert(`Couldn't check the client email, so nothing was voided or sent: ${e.message}`); return; }
    if (!newEmail) { alert("No client email on this quote. Add one under Edit contact details first."); return; }
    if (isTestQuote(q) && !isInternalOnlySend(newEmail)) { alert(TEST_QUOTE_SEND_BLOCKED); return; }
    const lines = stale.map(s => `${SEND_LOG_METHOD[s.docType]}: sent to ${s.to}`).join("\n");
    if (!window.confirm(`Void and resend for ${quoteNumber}?\n\n${lines}\n\nThese are voided in GoodSign (no email to the old address), logged in History, then sent again to:\n${newEmail}`)) return;
    setVoiding(true);
    try { await sendContractRequest(quoteNumber, { action: 'voidAndResend', expectEmail: newEmail }); }
    finally { setVoiding(false); }
  }

  // ── RAMS status (on-site Contracted jobs) ───────────────────────────────
  // In-unit jobs get no RAMS; saved quotes without a location count as on-site.
  function isOnSiteQuote(q) {
    try { return (JSON.parse(q["State JSON"] || "{}").job?.jobLocation || "on-site") !== "in-unit"; } catch (e) { return true; }
  }
  // "RAMS signed" in History (written by the webhook, or the catch-up) is the
  // reliable record; GoodSign's own list of signed documents can't be relied
  // on to include them, so its answer is only a fallback.
  const ramsSignedInHistory = React.useMemo(
    () => new Set(statusLog.filter(e => /^RAMS signed/.test(e.reason || "")).map(e => e.quoteNumber)),
    [statusLog]
  );
  const ramsOf = qn => (ramsSignedInHistory.has(qn) || ramsStatus[qn] === "signed-flag") ? "signed" : ramsStatus[qn];
  const ramsFlagOnly = qn => !ramsSignedInHistory.has(qn) && ramsStatus[qn] === "signed-flag";
  // Contracted on-site jobs, and on-site Automotive jobs (optional RAMS, no
  // contract) once they're booked.
  function needsRamsCheck(q) {
    if (q["Is Current"] === "No" || !isOnSiteQuote(q)) return false;
    return q["Status"] === "Contracted" || (isAutomotive(q) && JOB_STATUSES.includes(q["Status"]));
  }
  async function checkRamsStatus(q) {
    const quoteNumber = q["Quote Number"];
    try {
      const res = await apiFetch('/api/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteNumber, action: 'check', stage: 'contracted' }),
      });
      const data = res.ok ? await res.json() : {};
      // "signed-flag": GoodSign has it signed but it isn't in History (real
      // quotes aren't written to - see catchUpSignedRams).
      if (data.pendingTo) setPendingTo(s => ({ ...s, [quoteNumber]: data.pendingTo }));
      setRamsStatus(s => ({ ...s, [quoteNumber]: data.ramsSigned ? (data.ramsRecorded === false ? "signed-flag" : "signed") : data.ramsPending ? "pending" : "none" }));
    } catch (e) {
      setRamsStatus(s => ({ ...s, [quoteNumber]: "none" }));
    }
  }

  // ── Contract status (Awaiting Signature indicator) ──────────────────────
  async function checkContractStatus(quoteNumber) {
    try {
      const res = await apiFetch('/api/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteNumber, action: 'check' }),
      });
      if (!res.ok) { setContractStatus(s => ({ ...s, [quoteNumber]: "none" })); return; }
      const data = await res.json();
      // "signed": GoodSign has this exact quote's contract signed, but the
      // catch-up only applies it to Test quotes for now (see contracts.js).
      const signedNotApplied = data.signedContractFound && !data.contracted;
      if (data.pendingTo) setPendingTo(s => ({ ...s, [quoteNumber]: data.pendingTo }));
      setContractStatus(s => ({ ...s, [quoteNumber]: signedNotApplied ? "signed" : (data.contractPending || data.ramsPending) ? "pending" : "none" }));
      // The check found a signed contract whose signing notification never
      // arrived, and set the quote to Contracted (see catchUpSignedContract).
      if (data.contracted) {
        const patch = { Status: "Contracted", "Date Updated": new Date().toLocaleDateString("en-GB") };
        setQuotes(qs => qs.map(q => q["Quote Number"] === quoteNumber ? { ...q, ...patch } : q));
        setSelectedQuote(prev => prev && prev["Quote Number"] === quoteNumber ? { ...prev, ...patch } : prev);
        fetchStatusLog().then(setStatusLog).catch(() => {});
      }
    } catch (e) {
      // Leave it unresolved rather than guessing - the button falls back to
      // its normal clickable state, and the send path's own duplicate check
      // still guards it either way.
      setContractStatus(s => ({ ...s, [quoteNumber]: "none" }));
    }
  }

  // Only quotes actually showing an Accepted-status row need checking, and
  // each one only needs to be checked once - marking it "checking"
  // synchronously (before the async request resolves) is what stops this
  // effect from re-firing the same request on every re-render.
  // On-site Contracted jobs: is the RAMS out, signed, or neither?
  useEffect(() => {
    const toCheck = quotes.filter(q => needsRamsCheck(q) && !(q["Quote Number"] in ramsStatus));
    if (toCheck.length === 0) return;
    setRamsStatus(s => { const next = { ...s }; toCheck.forEach(q => { next[q["Quote Number"]] = "checking"; }); return next; });
    toCheck.forEach(q => checkRamsStatus(q));
  }, [quotes, ramsStatus]);

  useEffect(() => {
    const toCheck = quotes
      .filter(q => q["Status"] === "Accepted" && !isAutomotive(q))
      .map(q => q["Quote Number"])
      .filter(qn => qn && !(qn in contractStatus));
    if (toCheck.length === 0) return;
    setContractStatus(s => {
      const next = { ...s };
      toCheck.forEach(qn => { next[qn] = "checking"; });
      return next;
    });
    toCheck.forEach(qn => checkContractStatus(qn));
  }, [quotes, contractStatus]);

  // ── Dashboard: workflow strip + Needs attention ─────────────────────────
  // Current versions only, so an amended quote counts once.
  const money0 = n => "£" + Math.round(n).toLocaleString("en-GB");
  const exVat = q => parseFloat(String(q["Total (ex VAT)"] || "0").replace(/[£,]/g, "")) || 0;
  const workflow = React.useMemo(() => {
    const current = quotes.filter(q => q["Is Current"] !== "No" && !isTestQuote(q));
    const stage = (label, statuses) => {
      const list = current.filter(q => statuses.includes(q["Status"]));
      return { label, statuses, count: list.length, value: list.reduce((n, q) => n + exVat(q), 0) };
    };
    const stages = [
      stage("Quotes", ["Draft", "Amended", "Sent"]),
      stage("Jobs", JOB_STATUSES),
      stage("Invoices", ["Invoiced"]),
      stage("Paid", ["Complete"]),
    ];
    // Revenue pipeline: quotes out with clients plus everything won and not
    // yet paid. Closed (Lost) quotes are never counted.
    const pipeline = current.filter(q => ["Sent", ...JOB_STATUSES, "Invoiced"].includes(q["Status"])).reduce((n, q) => n + exVat(q), 0);
    // Conversion: won ÷ (won + lost), real current quotes only.
    return { stages, pipeline, conversion: conversionStats(quotes) };
  }, [quotes]);

  // Things that need doing: quotes sent a week+ ago with no reply, contracts
  // out for signature, and dry ice orders due (or overdue) to be placed.
  const attention = React.useMemo(() => {
    const items = [];
    const today = toISODate(new Date());
    const daysAgo = ts => { const d = parseDate(String(ts || "").slice(0, 10)) || parseDate(ts); return d ? Math.floor((Date.now() - d.getTime()) / 86400000) : null; };
    quotes.filter(q => q["Is Current"] !== "No" && !isTestQuote(q)).forEach(q => {
      if (q["Status"] === "Sent") {
        const last = lastSendFor(sendLog, q["Quote Number"]);
        const age = daysAgo(last?.timestamp || q["Date Updated"]);
        if (age != null && age >= FOLLOW_UP_OR_LOST_DAYS) items.push({ q, kind: "followupLost", text: `Sent ${age} days ago: follow up or mark lost` });
        else if (age != null && age >= 7) items.push({ q, kind: "followup", text: `Sent ${age} days ago, no reply yet` });
      }
      if (q["Status"] === "Accepted" && contractStatus[q["Quote Number"]] === "pending") items.push({ q, kind: "contract", text: "Contract awaiting signature" });
      // On-site job starting within 7 days and its RAMS isn't signed.
      if (needsRamsCheck(q) && !isAutomotive(q) && ["pending", "none"].includes(ramsOf(q["Quote Number"]))) {
        const { start } = getJobDates(q);
        const inWeek = toISODate(new Date(Date.now() + 7 * 86400000));
        if (start && start >= today && start <= inWeek) items.push({ q, kind: "rams", text: `RAMS not signed, job ${start === today ? "today" : `on ${fmtDay(start)}`}` });
      }
      const flagged = jobFlags[baseNumber(q["Quote Number"])];
      if (flagged) items.push({ q, kind: "finding", text: `${flagged} job ${flagged === 1 ? "note" : "notes"} the client still needs to be told about` });
      if (JOB_STATUSES.includes(q["Status"])) {
        const po = poOrderBy(q, suppliers);
        if (po && (po.state === "due" || (po.state === "pending" && po.date <= today))) items.push({ q, kind: "po", text: `Dry ice order due by ${fmtDay(po.date)}` });
      }
    });
    return items;
  }, [quotes, sendLog, contractStatus, ramsStatus, ramsSignedInHistory, suppliers, jobFlags]);

  // Click the active column to flip it; a new column starts newest/highest
  // first for dates and totals, A to Z otherwise.
  const sortHeader = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key.startsWith("Date ") || key.includes("Total") ? "desc" : "asc"); }
  };

  const SortIcon = ({ col }) => sortKey === col
    ? <span style={{ marginLeft: 5, color: C.brand, fontSize: 10 }}>{sortDir === "asc" ? "▲" : "▼"}</span>
    : <span style={{ marginLeft: 5, opacity: 0.25, fontSize: 10 }}>↕</span>;

  // ── Greeting ──────────────────────────────────────────────────────────────
  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const firstName = user?.firstName || user?.fullName?.split(" ")[0] || "there";

  // ── Styles ────────────────────────────────────────────────────────────────
  const S = {
    wrap: { fontFamily: "'IBM Plex Sans', sans-serif", color: C.text, background: C.bg, minHeight: "100vh", boxSizing: "border-box" },
    content: { padding: isMobile ? "20px 16px 40px" : "32px 40px 56px", maxWidth: 1440, margin: "0 auto" },
    controls: { display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" },
    input: {
      padding: "10px 14px", borderRadius: 12, border: `1px solid ${C.border}`,
      fontSize: 13, outline: "none", background: C.surface, color: C.text,
      flex: isMobile ? "1 1 100%" : "1 1 220px", minWidth: isMobile ? "100%" : 220, boxSizing: "border-box",
    },
    select: {
      padding: "10px 14px", borderRadius: 12, border: `1px solid ${C.border}`,
      fontSize: 13, background: C.surface, color: C.text, cursor: "pointer",
      flex: isMobile ? "1 1 47%" : "0 0 auto",
    },
    resultsCount: {
      fontSize: 12, color: C.textMuted, background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 999, padding: "6px 14px", whiteSpace: "nowrap",
      marginLeft: isMobile ? 0 : "auto", flex: isMobile ? "1 1 100%" : "0 0 auto",
      textAlign: isMobile ? "left" : "right", boxSizing: "border-box",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    th: {
      textAlign: "left", padding: "12px 10px", background: "transparent", color: C.textFaint,
      fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
      cursor: "pointer", whiteSpace: "nowrap", userSelect: "none", borderBottom: `1px solid ${C.border}`,
    },
    td: {
      color: C.text,
      padding: "11px 10px", borderBottom: `1px solid ${C.border}`,
      verticalAlign: "middle",
    },
    actionBtnPrimary: {
      padding: "5px 8px", borderRadius: 8, border: "none",
      background: C.brand, color: "#fff", fontSize: 11,
      fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
    },
    actionBtn: {
      padding: "5px 8px", borderRadius: 8, border: "none",
      background: "#EEF1F9", color: "#4A5A85", fontSize: 11,
      fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    },
    amendBtn: {
      padding: "5px 8px", borderRadius: 8, border: "none",
      background: "#F5EEFC", color: "#7C3AED", fontSize: 11,
      fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    },
    sendBtn: {
      padding: "5px 8px", borderRadius: 8, border: "none",
      background: "#FEF3E1", color: "#93630A", fontSize: 11,
      fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    },
    contractBtn: {
      padding: "5px 8px", borderRadius: 8, border: "none",
      background: "#E9EEFD", color: "#2B3E9E", fontSize: 11,
      fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    },
    successBanner: {
      padding: "10px 16px", background: "#E6F9F0", color: "#0D5C36",
      border: "1px solid #27AE60", borderRadius: 10, marginBottom: 12,
      fontSize: 13, fontWeight: 500,
    },
    errorBanner: {
      padding: "12px 16px", background: "#FEECEC", color: "#8B1A1A",
      border: "1px solid #E53935", borderRadius: 10, marginBottom: 12,
      fontSize: 13,
    },
    // Detail panel
    panel: {
      position: "fixed", top: 0, right: 0, width: isMobile ? "100%" : 400, height: "100vh",
      background: "#fff", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
      zIndex: 1000, overflowY: "auto", padding: 24, boxSizing: "border-box",
      fontFamily: "'IBM Plex Sans', sans-serif",
    },
    panelClose: {
      position: "absolute", top: 16, right: 16, background: "none",
      border: "none", fontSize: 20, cursor: "pointer", color: "#666", lineHeight: 1,
    },
    overlay: {
      position: "fixed", inset: 0, background: "rgba(20,25,45,0.35)", zIndex: 999,
    },
    statusSelect: {
      padding: "6px 10px", borderRadius: 8, border: `2px solid ${C.brand}`,
      fontSize: 13, fontWeight: 600, color: C.brand, background: "#F0F4FF",
      cursor: "pointer", width: "100%",
    },
  };

  // Row actions now live in the detail panel (click a row) and the row's ⋯
  // menu; amendLabel says when Amend is offered.
  // Accepted: only once we know no contract is out for signature (void it in
  // GoodSign first), since a pending contract is for the old version.
  const amendLabel = q => ["Draft", "Sent", "Amended"].includes(q["Status"])
    || (q["Status"] === "Accepted" && (isAutomotive(q) || contractStatus[q["Quote Number"]] === "none"));
  // Contract is a real action (sends a document) and stays here - only when
  // nothing is currently outstanding, since a pending signature isn't
  // something to click, it's a status. That readout now lives with the
  // status badge instead (see nextStepLine below), not the Actions
  // column - a status-shaped element sitting among action buttons competed
  // with the STATUS column's own badge for attention.

  // Small secondary tag shown under the main status badge - not a button,
  // just tells you a contract is out for signature without competing with
  // the badge itself or living among the real actionable buttons.
  // The one "what happens next" line under the status pill (the pill itself
  // only ever shows the lifecycle status). Amber = waiting on something or
  // someone; green = done but needs a look. The red PO Due tag stays separate.
  const todayISO = toISODate(new Date());
  const nextStep = q => {
    const qn = q["Quote Number"];
    const cs = contractStatus[qn], rs = ramsOf(qn);
    const { start, end } = getJobDates(q);
    switch (q["Status"] || "Draft") {
      case "Draft": return { text: "Next: send to client" };
      case "Amended": return { text: "Next: send the amended quote" };
      case "Sent": return { text: "Waiting for the client to accept" };
      case "Accepted":
        // Automotive: a deposit invoice instead of the GoodSign contract.
        if (isAutomotive(q)) return { text: "Next: send deposit invoice" };
        if (cs === "signed") return { text: "Contract signed in GoodSign (not applied)", tone: "green", title: "GoodSign shows this quote's Service Contract as signed, but the signing notification never reached the CRM. Not changed automatically for real quotes yet." };
        if (cs === "pending") return { text: "Contract awaiting signature" };
        if (cs === "none") return { text: "Next: send the contract" };
        return null; // still checking
      case "Contracted":
        if (isOnSiteQuote(q) && ramsFlagOnly(qn)) return { text: "RAMS signed in GoodSign (not recorded)", tone: "green", title: "GoodSign shows this job's RAMS as signed, but the signing notification never reached the CRM. Not recorded automatically for real quotes yet." };
        if (isOnSiteQuote(q) && rs === "pending") return { text: "RAMS awaiting signature" };
        if (isOnSiteQuote(q) && rs === "none") return { text: "RAMS not signed yet" };
        if (!start) return { text: "Next: set the job date" };
        if ((end || start) < todayISO) return { text: "Next: invoice" };
        return { text: `Job ${start === todayISO ? "today" : `on ${fmtDay(start)}`}` };
      case DEPOSIT_PENDING: return { text: "Deposit pending" };
      case DEPOSIT_PAID:
        if (!start) return { text: "Next: set the job date" };
        if ((end || start) < todayISO) return { text: "Next: mark complete when the balance is paid" };
        return { text: `Job ${start === todayISO ? "today" : `on ${fmtDay(start)}`}` };
      case "Invoiced": return { text: "Next: mark complete when paid" };
      default: return null;
    }
  };
  // How a manual status change reads as an action.
  const statusActionLabel = s => ({ [LOST]: "Mark lost", [DEPOSIT_PENDING]: "Deposit invoice sent", [DEPOSIT_PAID]: "Mark deposit paid", Complete: "Mark complete" }[s] || `Mark ${s}`);
  // "Price" under a Lost badge (old Rejected rows: "Reason not recorded").
  const lossLine = q => {
    const r = lossReasonOf(q);
    return r ? <span style={{ fontSize: 11, fontWeight: 600, color: TONE.red.text }} title={q["Loss Note"] || ""}>{r}</span> : null;
  };
  const nextStepLine = q => {
    const step = nextStep(q);
    if (!step) return null;
    const tone = step.tone === "green" ? TONE.green : TONE.amber;
    return (
      <span title={step.title || ""} style={{ background: tone.bg, color: tone.text, display: "inline-block", fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 999, border: `1px solid ${tone.border}`, whiteSpace: "nowrap", cursor: "default", userSelect: "none" }}>
        {step.text}
      </span>
    );
  };

  // Surfaces the daily Apps Script check's "due" flag (see PODueCheck.gs) -
  // a job whose dry-ice Order-by Date has passed without a PO having been
  // sent yet. Same small-tag treatment as nextStepLine, just a
  // different trigger and color, so the two read as one family of "needs
  // attention" indicators rather than competing styles.
  const poDueTag = q => (JOB_STATUSES.includes(q["Status"]) && parsePOData(q).poStatus === "due") ? (
    <span
      title="This job's dry-ice order-by date has passed without a PO being sent — raise one from the Dry ice section of the job's panel."
      style={{ background: TONE.red.bg, color: TONE.red.text, display: "inline-block", fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 999, border: `1px solid ${TONE.red.border}`, whiteSpace: "nowrap", cursor: "default", userSelect: "none" }}
    >
      PO Due
    </span>
  ) : null;

  const canSend = q => !!q["State JSON"] && q["Is Current"] !== "No";

  // Amend as the panel offers it: what it runs, or why it isn't offered.
  // Shared by the panel's More menu and the read-only Open banner.
  const latestOf = q => quotes.find(x => baseNumber(x["Quote Number"]) === baseNumber(q["Quote Number"]) && x["Is Current"] !== "No");
  const amendInfo = q => {
    if (q["Is Current"] !== "No") {
      if (amendLabel(q)) return { label: "Amend", run: () => handleAmend(q) };
      const st = q["Status"] || "Draft";
      const cs = contractStatus[q["Quote Number"]];
      if (st === "Accepted" && !isAutomotive(q) && cs === "pending") return { reason: "Void the contract in GoodSign before amending" };
      if (st === "Accepted" && !isAutomotive(q) && cs === "signed") return { reason: "The contract is signed in GoodSign, so this quote can't be amended" };
      if (st === "Accepted" && !isAutomotive(q)) return { reason: "Checking the contract status. Try again in a moment" };
      return { reason: `${st} quotes can't be amended` };
    }
    const latest = latestOf(q);
    if (latest && amendLabel(latest)) return { label: "Amend from this version", run: () => handleAmendFromVersion(q) };
    return { reason: latest ? `Earlier version · ${latest["Quote Number"]} is ${latest["Status"] || "Draft"}` : "Earlier version · no current version to amend" };
  };
  // The panel's one primary button: the action for the next step (see
  // nextStep), or none when the next step isn't ours to click (waiting on
  // the client, a signature, the job date). Contracted with no job date
  // offers Set job date even while RAMS is unsigned; once the date has
  // passed, Mark as invoiced. Status changes keep their reason prompt.
  const primaryAction = q => {
    if (q["Is Current"] === "No") return null;
    const qn = q["Quote Number"];
    const st = q["Status"] || "Draft";
    const { start, end } = getJobDates(q);
    if ((st === "Draft" || st === "Amended") && canSend(q)) return { key: "send", label: savingContact ? SAVING_CONTACT : sendingEmail[qn] ? "Sending…" : "Send to Client", disabled: !!sendingEmail[qn] || savingContact, run: () => openSendConfirm(q) };
    if (st === "Accepted" && isAutomotive(q)) return { key: "deposit", label: "Deposit invoice sent", disabled: updatingStatus, run: () => handleStatusChange(q, DEPOSIT_PENDING) };
    if (st === DEPOSIT_PENDING) return { key: "depositPaid", label: "Mark deposit paid", disabled: updatingStatus, run: () => handleStatusChange(q, DEPOSIT_PAID) };
    if (st === DEPOSIT_PAID && !start) return { key: "date", label: "Set job date", run: () => { setEditingDates(true); if (recordRef) setRecordScrollTo("jobStart"); else openRecord(q, "jobStart"); } };
    if (st === DEPOSIT_PAID && (end || start) < todayISO) return { key: "complete", label: "Mark complete", disabled: updatingStatus, run: () => handleStatusChange(q, "Complete") };
    if (st === "Accepted" && contractStatus[qn] === "none") return { key: "contract", label: savingContact ? SAVING_CONTACT : "Send Contract", disabled: savingContact, run: () => handleSendContract(qn) };
    // Job dates live on the record page: from the panel, open it there.
    if (st === "Contracted" && !start) return { key: "date", label: "Set job date", run: () => { setEditingDates(true); if (recordRef) setRecordScrollTo("jobStart"); else openRecord(q, "jobStart"); } };
    if (st === "Contracted" && (end || start) < todayISO) return { key: "invoiced", label: "Mark as invoiced", disabled: updatingStatus, run: () => handleStatusChange(q, "Invoiced") };
    if (st === "Invoiced") return { key: "complete", label: "Mark complete", disabled: updatingStatus, run: () => handleStatusChange(q, "Complete") };
    return null;
  };
  function focusJobStart() {
    const el = jobStartRef.current;
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus({ preventScroll: true }); }
  }
  useEffect(() => {
    if (!recordRef || !recordScrollTo || selectedQuote?.["Quote Number"] !== recordRef) return;
    const id = setTimeout(() => {
      if (recordScrollTo === "jobStart") focusJobStart();
      else document.getElementById(`record-${recordScrollTo}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      setRecordScrollTo(null);
    }, 50);
    return () => clearTimeout(id);
  }, [recordRef, recordScrollTo, selectedQuote, editingDates]);
  // Same tab, so the page's "← Back to Quote Log" returns here with this
  // quote's panel open again.
  // Both note the quote first, so the browser's Back button also reopens
  // its panel (see logFocus in App.jsx).
  const noteReturn = q => { try { window.sessionStorage.setItem("sublime_reopen", q["Quote Number"]); } catch (e) { /* storage blocked */ } };
  const openQuotePage = q => { noteReturn(q); window.location.assign(quotePreviewUrl(q["Quote Number"])); };
  // Job Sheet opens on screen in the same tab, like View Quote, with its own
  // Back and Print. The PDF opens its own print window (opened on the click
  // so pop-up blockers allow it).
  const printQuotePDF = async q => {
    const w = window.open("", "_blank");
    if (!w) { alert("Please allow pop-ups for this site to print the quote."); return; }
    try { printHTML(w, await generateQuoteDocumentHTML(q)); }
    catch (e) { w.close(); alert("Couldn't build the quote PDF: " + e.message); }
  };
  const printJobSheet = q => { noteReturn(q); window.location.assign(`/jobsheet/${encodeURIComponent(q["Quote Number"])}`); };

  // Headline/stats/hero broken into named blocks so mobile and desktop can
  // order them differently without duplicating the JSX itself: desktop
  // keeps the hero as a side panel next to headline+stats (visual order via
  // CSS grid columns); mobile is a plain top-to-bottom stack, where DOM
  // order IS visual order, and the hero needs to sit right after the
  // headline - before the stat cards - rather than pushed below all four.
  const headlineBlock = (
    <div>
      <div style={{ fontSize: isMobile ? 26 : 36, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
        Track every job,<br />one glance at a time
      </div>
      <div style={{ fontSize: 14, color: C.textMuted, marginTop: 8 }}>
        Good {timeOfDay}, {firstName}
      </div>
    </div>
  );
  const workflowBlock = (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10 }}>
        {workflow.stages.map((st, i) => (
          <div key={st.label} style={{ position: "relative", background: "#F7F8FC", border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textMuted }}>{st.label}{!isMobile && i < 3 && <span style={{ float: "right", color: C.textFaint }}>→</span>}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginTop: 4 }}>{st.count}</div>
            <div style={{ fontSize: 12, color: C.textMuted }}>{money0(st.value)} <span style={{ fontSize: 10 }}>ex VAT</span></div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 10 }}>Revenue pipeline, ex VAT (sent quotes, jobs and invoices): <strong style={{ color: C.text }}>{money0(workflow.pipeline)}</strong></div>
      {/* Quote-to-job conversion and why quotes are lost (real quotes only). */}
      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>
        Quote-to-job conversion: <strong style={{ color: C.text }}>{workflow.conversion.rate == null ? "no decided quotes yet" : `${Math.round(workflow.conversion.rate * 100)}%`}</strong>
        {workflow.conversion.rate != null && <> ({workflow.conversion.won} won, {workflow.conversion.lost} lost)</>}
      </div>
      {workflow.conversion.reasons.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {workflow.conversion.reasons.map(r => (
            <span key={r.reason} style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 999, background: TONE.red.bg, color: TONE.red.text, border: `1px solid ${TONE.red.border}` }}>{r.reason} · {r.count}</span>
          ))}
        </div>
      )}
    </div>
  );
  const attentionBlock = attention.length === 0 ? (
    <div style={{ fontSize: 12, color: C.textFaint }}>Nothing needs attention right now.</div>
  ) : (
    <div>
      {attention.map((a, i) => (
        <div key={`${a.kind}-${a.q["Quote Number"]}`} onClick={() => setSelectedQuote(a.q)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: i ? `1px solid ${C.border}` : "none", cursor: "pointer" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: a.kind === "po" || a.kind === "finding" ? "#D08A1E" : a.kind === "contract" ? "#3A52CC" : "#C0504D" }} />
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: C.text }}>
            <strong>{a.q["Quote Number"]}</strong> · {a.q["Client Name"] || "No client"}
            <div style={{ fontSize: 12, color: C.textMuted }}>{a.text}</div>
          </div>
          {a.kind === "followupLost" && (
            <button onClick={e => { e.stopPropagation(); handleStatusChange(a.q, LOST); }} style={{ ...S.actionBtn, padding: "7px 12px", fontSize: 12, minHeight: isMobile ? 40 : 30 }}>Mark lost</button>
          )}
          {a.kind === "finding" && (
            <button onClick={e => { e.stopPropagation(); openRecord(a.q); }} style={{ ...S.actionBtnPrimary, padding: "7px 12px", fontSize: 12, minHeight: isMobile ? 40 : 30 }}>Open job</button>
          )}
          {a.kind === "po" && (
            <button onClick={e => { e.stopPropagation(); setSelectedQuote(a.q); setShowRaisePOModal(true); }} style={{ ...S.actionBtnPrimary, padding: "7px 12px", fontSize: 12, minHeight: isMobile ? 40 : 30 }}>Raise PO</button>
          )}
        </div>
      ))}
    </div>
  );
  const heroBlock = (
    <div style={{ borderRadius: 24, overflow: "hidden", border: `1px solid ${C.border}`, boxShadow: "0 8px 24px rgba(20,30,70,0.06)", height: isMobile ? undefined : "100%" }}>
      <img
        src="/sublime-dry-ice-blasting-hero.jpg"
        alt="Sublime Precision Cleaning — dry ice blasting in progress"
        style={{ width: "100%", height: isMobile ? "100%" : 150, minHeight: isMobile ? 160 : 0, objectFit: "cover", objectPosition: "center 35%", display: "block" }}
      />
    </div>
  );

  // ── Record page: section contents ───────────────────────────────────────
  // Interactive pieces are built here (they use this component's drafts and
  // handlers); RecordPage lays them out.
  const fieldLabel = { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4, display: "block" };
  const fieldInput = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #d0d8e8", fontSize: 14, color: "#1a1a2e", boxSizing: "border-box", background: "#fff", fontFamily: "inherit", minHeight: 42 };
  const blockBtn = { padding: "10px 18px", borderRadius: 10, border: "none", background: C.brand, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", minHeight: 42 };
  const ghostBtn = { ...blockBtn, background: C.surface, color: C.text, border: `1px solid ${C.border}` };

  // Client: contact details, edited in place on this version (no new
  // version, price unchanged) - same save as before.
  function contactEditor(q) {
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          {[["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"]].map(([k, label]) => (
            <label key={k} style={{ gridColumn: k === "address" && !isMobile ? "1 / -1" : undefined }}>
              <span style={fieldLabel}>{label}</span>
              <input value={contactEdit[k]} onChange={e => setContactEdit(c => ({ ...c, [k]: e.target.value }))} style={fieldInput} />
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" style={{ ...blockBtn, opacity: savingContact || !contactEdit.name.trim() ? 0.6 : 1 }} disabled={savingContact || !contactEdit.name.trim()} onClick={() => saveContactEdit(q)}>{savingContact ? "Saving…" : "Save contact details"}</button>
          <button type="button" style={ghostBtn} disabled={savingContact} onClick={() => setContactEdit(null)}>Cancel</button>
          <span style={{ fontSize: 12, color: C.textFaint }}>Saved on this version. No new version, price unchanged.</span>
        </div>
      </div>
    );
  }

  // Job dates, after Edit dates: column X only (see handleSaveJobDates).
  function jobDatesEditor(q) {
    const cancel = () => { setJobDatesDraft(jobDatesStart(q)); setJobDatesError(null); setEditingDates(false); };
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "220px 220px", gap: 12 }}>
          <label style={{ minWidth: 0 }}>
            <span style={fieldLabel}>Job start</span>
            <input type="date" ref={jobStartRef} style={fieldInput} value={jobDatesDraft.jobStartDate}
              onChange={e => setJobDatesDraft(d => ({ ...d, jobStartDate: e.target.value }))} />
          </label>
          <label style={{ minWidth: 0 }}>
            <span style={fieldLabel}>Job end (optional)</span>
            <input type="date" style={fieldInput} value={jobDatesDraft.jobEndDate}
              min={jobDatesDraft.jobStartDate || undefined}
              onChange={e => setJobDatesDraft(d => ({ ...d, jobEndDate: e.target.value }))} />
          </label>
        </div>
        {jobDatesError && <div style={{ fontSize: 13, color: "#A16A66", marginTop: 8 }}>{jobDatesError}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button type="button" onClick={handleSaveJobDates} disabled={savingJobDates} style={{ ...blockBtn, opacity: savingJobDates ? 0.6 : 1 }}>{savingJobDates ? "Saving…" : "Save job dates"}</button>
          <button type="button" onClick={cancel} disabled={savingJobDates} style={ghostBtn}>Cancel</button>
        </div>
      </div>
    );
  }

  // Dry ice order details, after Edit on its row: supplier, destination,
  // delivery date, cost preview and Save PO Details (same handler as before).
  function poEditor(q) {
    const selectedSupplier = suppliers.find(s => s.id === poDraft.supplierId) || null;
    const resolvedAddress = poDraft.deliveryDestination === "Site"
      ? (q["Site Address"] || "—")
      : (selectedSupplier?.yardDeliveryAddress || "—");
    const orderByDate = selectedSupplier
      ? computeOrderByDate(poDraft.requestedDeliveryDate, selectedSupplier.defaultLeadTimeDays)
      : null;
    const poOut = ["sent", "confirmed"].includes(parsePOData(q).poStatus);
    // Preview quantity mirrors RaisePOModal's own default (the job's
    // calculator figure, else the supplier's minimum order). The real
    // quantity is only ever set on the Raise PO screen.
    const autoQtyKg = extractCalculatorDryIceKg(q);
    const previewQtyKg = autoQtyKg != null ? autoQtyKg : (selectedSupplier?.minimumOrderKg ? Number(selectedSupplier.minimumOrderKg) : null);
    const costPreview = selectedSupplier && previewQtyKg ? computePOTotalCost({ quantityOrderedKg: previewQtyKg, supplier: selectedSupplier }) : null;
    const activeSuppliers = suppliers.filter(s => s.active);
    const line = { display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, color: "#4A5568", padding: "3px 0" };
    return (
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) minmax(0,1fr)", gap: isMobile ? 16 : 28, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label>
            <span style={fieldLabel}>Supplier</span>
            <select style={{ ...fieldInput, cursor: "pointer" }} value={poDraft.supplierId} onChange={e => setPoDraft(d => ({ ...d, supplierId: e.target.value }))}>
              <option value="">Select supplier…</option>
              {activeSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {activeSuppliers.length === 0 && <span style={{ display: "block", fontSize: 12, color: C.textFaint, marginTop: 4 }}>No active suppliers set up yet: add one in Admin.</span>}
          </label>
          <div>
            <span style={fieldLabel}>Delivery destination</span>
            <div style={{ display: "flex", gap: 6 }}>
              {["Yard", "Site"].map(dest => (
                <button key={dest} type="button" onClick={() => setPoDraft(d => ({ ...d, deliveryDestination: dest }))}
                  style={{ flex: 1, minHeight: 40, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    border: poDraft.deliveryDestination === dest ? "none" : `1px solid ${C.border}`,
                    background: poDraft.deliveryDestination === dest ? C.brand : C.surface,
                    color: poDraft.deliveryDestination === dest ? "#fff" : "#4a6483" }}>
                  {dest}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12.5, color: C.textMuted, marginTop: 6 }}>{resolvedAddress}</div>
            {poDraft.deliveryDestination === "Site" && !hasDeliveredTiers(selectedSupplier) && (
              <div style={{ marginTop: 8, padding: "8px 12px", background: "#FEF3E1", border: "1px solid #F5D28C", borderRadius: 8, fontSize: 12.5, color: "#93630A" }}>
                ⚠ The delivery surcharge in the cost preview is {selectedSupplier?.name || "the supplier"}'s Yard/TR27 rate. It may not be correct for this address: treat it as an estimate and confirm with the supplier if it matters for this order.
              </div>
            )}
          </div>
          <label>
            <span style={fieldLabel}>Requested delivery date</span>
            <input type="date" style={fieldInput} value={poDraft.requestedDeliveryDate} onChange={e => setPoDraft(d => ({ ...d, requestedDeliveryDate: e.target.value }))} />
          </label>
          {selectedSupplier && poDraft.requestedDeliveryDate && (
            <div style={{ padding: "10px 12px", background: "#F0F4FF", border: "1px solid #d0d8e8", borderRadius: 10, fontSize: 13, color: "#1D3461" }}>
              Order by <strong>{orderByDate ? orderByDate.toLocaleDateString("en-GB") : "—"}</strong>, before {selectedSupplier.orderCutoffTime || "the supplier's cutoff time"}. {selectedSupplier.name}'s lead time is {selectedSupplier.defaultLeadTimeDays} day{selectedSupplier.defaultLeadTimeDays === "1" ? "" : "s"}; approving after cutoff on that day still misses that day's slot.
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Cost preview - the last thing seen before Save/Raise, since
              approving on the Raise PO screen authorises an immediate
              payment to the supplier (no credit account). */}
          {costPreview ? (
            <div style={{ padding: "14px 16px", background: "#F7F8FC", border: `1px solid ${C.border}`, borderRadius: 12 }}>
              <div style={{ ...fieldLabel, marginBottom: 8 }}>PO cost preview{poOut ? " (if raising another)" : ", authorises immediate payment"}</div>
              <div style={line}><span>Quantity required</span><span>{fmtKg(previewQtyKg)}kg{autoQtyKg == null ? " (supplier minimum)" : ""}</span></div>
              <div style={line}><span>Quantity ordered ({costPreview.tiered ? "tier" : "bin-rounded"})</span><span>{fmtKg(costPreview.orderedKg)}kg</span></div>
              <div style={{ fontSize: 13, color: C.text, margin: "6px 0" }}>{costPreview.planLabel}</div>
              {costPreview.lines.map(([label, val]) => (
                <div key={label} style={line}><span>{label}</span><span>£{Number(val).toFixed(2)}</span></div>
              ))}
              <div style={{ ...line, fontSize: 14, fontWeight: 800, color: "#1D3461", marginTop: 6, paddingTop: 8, borderTop: `1px solid ${C.border}` }}><span>Total cost</span><span>£{costPreview.totalCost.toFixed(2)}</span></div>
            </div>
          ) : selectedSupplier && (
            <div style={{ fontSize: 12.5, color: C.textFaint }}>
              {previewQtyKg == null
                ? "No quantity to preview cost: this job has no calculator dry-ice figure and the supplier has no minimum order set. Quantity is entered on the Raise PO screen."
                : `${selectedSupplier.name} has no ${hasDeliveredTiers(selectedSupplier) ? "delivered tiers" : "bin sizes"} set up in Admin: add at least one to preview cost.`}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={handleSavePODetails} disabled={savingPO} style={{ ...blockBtn, opacity: savingPO ? 0.6 : 1 }}>
              {savingPO ? "Saving…" : "Save PO Details"}
            </button>
            <button type="button" onClick={() => setEditingPO(false)} disabled={savingPO} style={ghostBtn}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // A pending contract / RAMS out to an old address (the contact email has
  // changed since): say so, and offer void + resend.
  function staleAlert(q) {
    const stale = stalePending(q);
    if (!stale.length) return null;
    const sameTo = stale.every(s => s.to === stale[0].to);
    const what = stale.map(s => s.docType === "contract" ? "Contract" : "RAMS").join(" and ");
    return (
      <div role="alert" style={{ padding: "14px 16px", background: "#FFF7E6", border: "1px solid #F5A623", borderRadius: 12, color: "#7B5800", fontSize: 14, lineHeight: 1.45 }}>
        <div style={{ fontWeight: 700 }}>
          {sameTo ? <>{what} sent to {stale[0].to}, which no longer matches</> : stale.map(s => <div key={s.docType}>{s.docType === "contract" ? "Contract" : "RAMS"} sent to {s.to}, which no longer matches</div>)}
        </div>
        <div style={{ marginTop: 2 }}>The client email is now {q["Client Email"]}.</div>
        <button type="button" style={{ ...ghostBtn, marginTop: 10, opacity: voiding || savingContact ? 0.6 : 1 }} disabled={voiding || savingContact} onClick={() => handleVoidAndResend(q)}>
          {savingContact ? SAVING_CONTACT : voiding ? "Voiding and resending…" : `Void and resend to ${q["Client Email"]}`}
        </button>
      </div>
    );
  }

  // GoodSign documents: where the Service Contract and RAMS are up to.
  function documentItems(q) {
    const qn = q["Quote Number"];
    const st = q["Status"] || "Draft";
    const cs = contractStatus[qn];
    const pt = pendingTo[qn] || {};
    const signedStage = ["Contracted", "Invoiced", "Complete"].includes(st);
    const onSite = isOnSiteQuote(q);
    // Latest SendLog row for a document ("Contract" = both, before Oct 2026).
    const sentLine = method => {
      let last = null;
      sendLog.forEach(e => {
        const m = String(e.method || "").replace(/ \(on file\)$/, "");
        if (e.quoteNumber === qn && (m === method || m === "Contract") && (!last || e.timestamp > last.timestamp)) last = e;
      });
      return last ? `Sent ${formatUkTimestamp(last.timestamp)}${last.to ? ` to ${last.to}` : ""}` : "";
    };
    const notYet = { tone: "off", text: "Not sent yet" };
    // Automotive: no Service Contract (a deposit invoice instead); the RAMS
    // is optional, on-site jobs only, sent from Actions.
    if (isAutomotive(q)) {
      let autoRams;
      if (!onSite) autoRams = { tone: "off", text: "Not needed", detail: "In-unit job" };
      else {
        const r = ramsOf(qn);
        if (r === "signed") autoRams = { tone: "done", text: "Signed", detail: sentLine("RAMS") };
        else if (r === "pending" || pt.rams) autoRams = { tone: "waiting", text: "Awaiting signature", detail: pt.rams ? `Sent to ${pt.rams}` : sentLine("RAMS") };
        else autoRams = { tone: "off", text: "Optional, not sent", detail: sentLine("RAMS") || "Send RAMS from Actions if this job needs one" };
      }
      return [{ label: "Service Contract", tone: "off", text: "Not needed", detail: "Automotive: deposit invoice instead" }, { label: "RAMS", ...autoRams }];
    }
    let contract;
    if (signedStage) contract = { tone: "done", text: "Signed", detail: sentLine("Service Contract") };
    else if (st === "Accepted") {
      if (cs === "pending") contract = { tone: "waiting", text: "Awaiting signature", detail: pt.contract ? `Sent to ${pt.contract}` : sentLine("Service Contract") };
      else if (cs === "signed") contract = { tone: "done", text: "Signed in GoodSign", detail: "Not applied here yet: the signing notification never arrived." };
      else if (cs === "none") contract = notYet;
      else contract = { tone: "off", text: "Checking GoodSign…" };
    } else if (isLostStatus(st)) contract = { tone: "off", text: "Not needed", detail: "Quote lost" };
    else contract = { ...notYet, detail: "Sent once the client accepts" };

    let rams;
    if (!onSite) rams = { tone: "off", text: "Not needed", detail: "In-unit job" };
    else if (signedStage) {
      const r = ramsOf(qn);
      if (r === "signed") rams = { tone: "done", text: "Signed", detail: ramsFlagOnly(qn) ? "Signed in GoodSign, not recorded in History yet" : sentLine("RAMS") };
      else if (st !== "Contracted") rams = { tone: "off", text: "No signature recorded" };
      else if (r === "pending") rams = { tone: "waiting", text: "Awaiting signature", detail: pt.rams ? `Sent to ${pt.rams}` : sentLine("RAMS") };
      else if (r === "none") rams = { tone: "waiting", text: "Not signed yet", detail: sentLine("RAMS") };
      else rams = { tone: "off", text: "Checking GoodSign…" };
    } else if (st === "Accepted") {
      if (pt.rams) rams = { tone: "waiting", text: "Awaiting signature", detail: `Sent to ${pt.rams}` };
      else if (cs === "none") rams = { ...notYet, detail: "Goes with the Service Contract" };
      else if (cs === "pending") rams = { tone: "waiting", text: "Sent with the contract", detail: sentLine("RAMS") };
      else rams = { tone: "off", text: cs === "signed" ? "See GoodSign" : "Checking GoodSign…" };
    } else if (isLostStatus(st)) rams = { tone: "off", text: "Not needed", detail: "Quote lost" };
    else rams = { ...notYet, detail: "Goes with the Service Contract" };

    return [{ label: "Service Contract", ...contract }, { label: "RAMS", ...rams }];
  }

  // Subcontractors & Suppliers: one row per order. Column X holds a single
  // dry ice PO per quote today, so there's at most one row; the row model is
  // ready for more once orders are stored as a list.
  function supplyProps(q) {
    const po = parsePOData(q);
    const isSuperseded = q["Is Current"] === "No";
    const eligible = JOB_STATUSES.includes(q["Status"]) && !isSuperseded;
    const sup = suppliers.find(s => s.id === po.supplierId) || null;
    const out = ["sent", "confirmed"].includes(po.poStatus);
    const hasDetails = out || po.poStatus === "due" || !!po.supplierId;
    const rows = [];
    if (hasDetails || (eligible && usesDryIce(q))) {
      const needKg = extractCalculatorDryIceKg(q);
      let estimate = null;
      try { estimate = !out && sup && needKg ? computePOTotalCost({ quantityOrderedKg: needKg, supplier: sup }) : null; } catch (e) { /* no bin sizes */ }
      const orderBy = !out && sup && po.requestedDeliveryDate ? computeOrderByDate(po.requestedDeliveryDate, sup.defaultLeadTimeDays) : null;
      const status = po.poStatus === "confirmed" ? { tone: "done", status: "Confirmed" }
        : po.poStatus === "sent" ? { tone: "info", status: `Sent ${po.poSentTimestamp ? new Date(po.poSentTimestamp).toLocaleDateString("en-GB") : ""}`.trim() }
        : po.poStatus === "due" ? { tone: "problem", status: "Order overdue" }
        : orderBy ? { tone: "waiting", status: `Order by ${orderBy.toLocaleDateString("en-GB")}` }
        : { tone: "off", status: "Not ordered" };
      const actions = [];
      if (eligible && !out && !editingPO) actions.push({ label: hasDetails ? "Edit" : "Set up", run: () => setEditingPO(true) });
      if (eligible && po.poStatus === "sent" && isAdmin) actions.push({ label: confirmingPO ? "Saving…" : "Mark confirmed", disabled: confirmingPO, run: handleMarkPOConfirmed });
      rows.push({
        key: "dry-ice",
        supplier: sup?.name || (po.supplierId ? "Supplier" : "Not chosen yet"),
        detail: [po.deliveryDestination && `${po.deliveryDestination} delivery`, po.requestedDeliveryDate && fmtDay(po.requestedDeliveryDate), po.approvedBy && `approved by ${po.approvedBy}`].filter(Boolean).join(" · "),
        type: "Dry ice",
        quantity: out && po.quantityOrderedKg != null ? `${fmtKg(po.quantityOrderedKg)}kg` : needKg != null ? `${fmtKg(needKg)}kg needed` : "—",
        cost: out && po.totalCost != null ? `£${Number(po.totalCost).toFixed(2)}` : estimate ? `est. £${estimate.totalCost.toFixed(2)}` : "—",
        ...status,
        actions,
      });
    }
    return {
      rows,
      note: isSuperseded ? "Earlier version: orders are on the current version." : eligible ? "No dry ice on this job." : "Orders open once the quote is accepted.",
      raise: eligible && isAdmin ? { label: out ? "Raise another PO" : "Raise PO", run: () => setShowRaisePOModal(true) } : null,
      editBlock: eligible && editingPO && poDraft ? poEditor(q) : null,
    };
  }

  // ── Google review request (api/review-request.js) ────────────────────────
  async function handleSendReview(q, alreadySent) {
    const to = q["Client Email"];
    if (!to) { window.alert("There's no client email on this quote."); return; }
    if (!window.confirm(`${alreadySent ? "A review request was already sent. Send it again" : "Send the Google review request"} to ${to}?`)) return;
    setSendingReview(true);
    try {
      const res = await apiFetch("/api/review-request", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ref: q["Quote Number"], force: alreadySent, by: changedByLabel }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.result !== "sent") throw new Error(data.reason || data.error || `Error ${res.status}`);
      setStatusLog(l => [...l, { quoteNumber: q["Quote Number"], timestamp: new Date().toISOString(), changedBy: changedByLabel, fromStatus: q["Status"], toStatus: q["Status"], reason: `Review request sent to ${to} (sent by hand)` }]);
      setSuccessMsg(`Review request sent to ${to}`);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) {
      window.alert(`Couldn't send the review request: ${e.message}`);
    } finally {
      setSendingReview(false);
    }
  }
  // WhatsApp: the phone's share sheet (or WhatsApp on a laptop) with the
  // message and review link written.
  async function openReviewWhatsApp(q) {
    const st = parseStateJson(q) || {};
    const name = String(st.client?.contactName || q["Client Name"] || "").trim().split(/\s+/)[0];
    const v = [st.vehicle?.make, st.vehicle?.model].filter(Boolean).join(" ");
    const what = q["Category"] === "Automotive" ? (v ? `your ${v}` : "your vehicle") : "the job";
    const text = `${name ? `Hi ${name}` : "Hi"}, thank you for choosing Sublime Precision for ${what}. We hope you're pleased with the result. If you have a minute, a Google review really helps a small Cornish business like ours: ${GOOGLE_REVIEW_LINK} And if anything isn't quite right, just let us know and we'll sort it. ${({ Edward: "Ed" })[(user?.firstName || "")] || user?.firstName || "Ed"}, Sublime Precision`;
    if (isMobile && navigator.share) { try { await navigator.share({ text }); } catch (e) { /* cancelled */ } return; }
    let num = String(q["Client Phone"] || "").replace(/[^\d+]/g, "");
    if (num.startsWith("+")) num = num.slice(1); else if (num.startsWith("00")) num = num.slice(2); else if (num.startsWith("0")) num = "44" + num.slice(1);
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  }

  // Everything you can do to this job, for the Job Record's header menu
  // (the primary action sits beside it, so it isn't repeated here).
  function recordActions(q) {
    const qn = q["Quote Number"];
    const st = parseStateJson(q);
    const isSuperseded = q["Is Current"] === "No";
    const primary = primaryAction(q);
    const busy = savingContact ? SAVING_CONTACT : null;
    const contractReady = q["Status"] === "Accepted" && !isAutomotive(q) && contractStatus[qn] !== "pending" && !isSuperseded;
    const primaryStatus = { invoiced: "Invoiced", complete: "Complete", deposit: DEPOSIT_PENDING, depositPaid: DEPOSIT_PAID }[primary?.key];
    // On-site Automotive: an optional RAMS, while it isn't already out or signed.
    const ramsReady = isAutomotive(q) && isOnSiteQuote(q) && JOB_STATUSES.includes(q["Status"]) && !isSuperseded
      && !["pending", "signed", "checking"].includes(ramsOf(qn)) && !(pendingTo[qn] || {}).rams;
    const list = [];
    if (!isSuperseded) manualTransitions(q).filter(s => s !== primaryStatus)
      .forEach(s => list.push({ label: statusActionLabel(s), disabled: updatingStatus, run: () => handleStatusChange(q, s) }));
    if (canSend(q) && primary?.key !== "send") list.push({ label: busy || (sendingEmail[qn] ? "Sending…" : "Send to Client"), disabled: !!sendingEmail[qn] || savingContact, run: () => openSendConfirm(q) });
    if (contractReady && primary?.key !== "contract") list.push({ label: busy || "Send Contract", disabled: savingContact, run: () => handleSendContract(qn) });
    if (ramsReady) list.push({ label: busy || "Send RAMS", disabled: savingContact, run: () => handleSendContract(qn, { ramsOnly: true }) });
    if (!isSuperseded && q["Status"] === "Draft") list.push({ label: busy || "Mark as sent (no email)", disabled: updatingStatus || savingContact, run: () => handleMarkAsSent(q) });
    // Complete: the Google review request (sent automatically about a day
    // after completion; these send it now, by email or on WhatsApp).
    if (!isSuperseded && q["Status"] === "Complete") {
      const sent = statusLog.some(e => e.quoteNumber === qn && /^Review request sent/.test(e.reason || ""));
      list.push({ label: sendingReview ? "Sending…" : sent ? "Send review request again" : "Send review request", disabled: sendingReview, run: () => handleSendReview(q, sent) });
      list.push({ label: "Review request on WhatsApp", run: () => openReviewWhatsApp(q) });
    }
    const docs = [{ label: "View Quote ↗", run: () => openQuotePage(q) }, ...(st ? [{ label: "PDF", run: () => printQuotePDF(q) }] : []), { label: "Job Sheet", run: () => printJobSheet(q) }];
    docs[0].divider = list.length > 0;
    return [...list, ...docs];
  }

  function recordProps(q) {
    const qn = q["Quote Number"];
    const st = parseStateJson(q);
    const isAuto = q["Category"] === "Automotive";
    const isSuperseded = q["Is Current"] === "No";
    const onSite = isOnSiteQuote(q);
    const primary = primaryAction(q);
    const contact = quoteContactName(q);
    // What was actually charged, and the discount that got it there (same
    // resolveDiscount the quote document uses). The Margin % column holds
    // the target margin; the real margin is after any discount.
    const cb = st?.costBreakdown || null;
    const finalExVat = parseFloat(String(q["Total (ex VAT)"] || "").replace(/[^0-9.]/g, "")) || 0;
    const discount = resolveDiscount(st?.commercial, finalExVat);
    const preDiscountSubtotal = discount ? discount.subtotalExVat : finalExVat;
    const actualMargin = cb && finalExVat > 0 && cb.totalCost != null ? (finalExVat - cb.totalCost) / finalExVat * 100 : null;
    const last = lastSendFor(sendLog, qn);
    const { start, end } = getJobDates(q);
    const fullScope = st ? resolveClientScope(st) : "";
    const scheduled = getScheduledDate(q);
    return {
      refNo: qn, q, isSuperseded,
      // Unsent new version: which version the client actually has.
      clientHas: clientHasLabel(versionClientHas(q, quotes, sendLog)),
      // Lost: reason beside the status, note under it. Otherwise the newest
      // note typed with a status change.
      lossReason: lossReasonOf(q),
      lossNote: isLostStatus(q["Status"]) ? String(q["Loss Note"] || "").trim() : "",
      statusNote: (() => { const n = latestStatusNote(statusLog, qn); return n ? { text: n.text, when: formatUkTimestamp(n.timestamp) } : null; })(),
      latest: isSuperseded ? latestOf(q) : null,
      primary,
      nextStep: nextStep(q),
      actions: recordActions(q),
      client: {
        name: contact || q["Client Name"],
        company: contact && contact !== q["Client Name"] ? q["Client Name"] : "",
        email: q["Client Email"], phone: quotePhone(q),
        address: (isAuto ? st?.client?.address : st?.job?.clientAddress) || "",
      },
      onEditContact: !isSuperseded && st ? () => startContactEdit(q, st) : null,
      contactEditBlock: !isSuperseded && contactEdit ? contactEditor(q) : null,
      job: {
        category: q["Category"],
        workType: st?.job?.workType,
        location: st ? (onSite ? "On-site (generator)" : "In-unit (mains power)") : "",
        application: q["Application"],
        vehicle: isAuto ? [st?.vehicle?.year, st?.vehicle?.make, st?.vehicle?.model].filter(Boolean).join(" ") + (st?.vehicle?.reg ? ` · ${String(st.vehicle.reg).toUpperCase()}` : "") : "",
        siteAddress: onSite ? q["Site Address"] : "",
        notes: q["Notes"],
        created: q["Date Created"], updated: q["Date Updated"],
        // No job start saved: the calculator's Scheduled Date, if any.
        start: start ? `${fmtDay(start)}${!parsePOData(q).jobStartDate && scheduled ? " (scheduled date on the quote)" : ""}` : "",
        end: end && end !== start ? fmtDay(end) : "",
      },
      onEditDates: !isSuperseded ? () => setEditingDates(true) : null,
      jobDatesEditBlock: !isSuperseded && editingDates ? jobDatesEditor(q) : null,
      scope: { html: fullScope ? scopeTextToHTML(fullScope) : "", text: q["Scope Summary"] || "" },
      fin: {
        cb, finalExVat, discount, preDiscountSubtotal,
        totalExVat: q["Total (ex VAT)"], totalIncVat: q["Total (inc VAT)"],
        margin: actualMargin != null ? `${actualMargin.toFixed(1)}%` : q["Margin %"],
        showRounded: !!cb && (!!discount || Math.abs(preDiscountSubtotal - (cb.afterCont || 0)) > 0.005),
        mileage: q["Mileage"], mileageCost: q["Mileage Cost"],
      },
      delivery: {
        printed: q["PDF Generated"], emailSent: q["Email Sent"], dateSent: q["Date Sent"],
        lastSentTo: last ? `${last.to}${formatUkTimestamp(last.timestamp) ? ` · ${formatUkTimestamp(last.timestamp)}` : ""}${last.method ? ` (${last.method})` : ""}` : "",
      },
      documents: { items: documentItems(q), staleBlock: staleAlert(q) },
      supply: supplyProps(q),
      history: quoteHistory(q).map(e => ({
        when: e.dayOnly ? (fmtShortDate(e.version["Date Created"]) || "") : formatUkTimestamp(e.t),
        text: e.text,
        versionNumber: e.version ? e.version["Quote Number"] : null,
        isThis: !!e.version && e.version["Quote Number"] === qn,
      })).reverse(),
    };
  }

  // Send to Client and Raise PO dialogs: shared by the list and the record.
  const dialogs = (
    <>
      {statusDialog?.kind === "lost" && (
        <LostDialog quote={statusDialog.quote} busy={updatingStatus} onClose={() => setStatusDialog(null)}
          onConfirm={lost => applyStatusChange(statusDialog.quote, LOST, { lost })} />
      )}
      {statusDialog?.kind === "note" && (
        <StatusNoteDialog quote={statusDialog.quote} newStatus={statusDialog.newStatus} busy={updatingStatus} onClose={() => setStatusDialog(null)}
          onConfirm={reason => applyStatusChange(statusDialog.quote, statusDialog.newStatus, { reason })} />
      )}
      {convertRejected && (
        <ConvertRejectedDialog rows={convertRejected.rows} done={convertRejected.done} busy={convertRejected.busy}
          onClose={() => setConvertRejected(null)} onConfirm={runConvertRejected} />
      )}
      {confirmSend && (
        <SendToClientModal
          quote={confirmSend}
          sending={!!sendingEmail[confirmSend["Quote Number"]]}
          onClose={() => setConfirmSend(null)}
          onConfirm={(overrides) => handleSendToClient(confirmSend, overrides)}
        />
      )}
      {showRaisePOModal && selectedQuote && (
        <RaisePOModal
          quote={selectedQuote}
          poDraft={poDraft}
          suppliers={suppliers}
          approverName={approverName}
          onClose={() => setShowRaisePOModal(false)}
          onApproved={(poData) => {
            const poDataStr = JSON.stringify(poData);
            setQuotes(qs => qs.map(q => q["Quote Number"] === selectedQuote["Quote Number"] ? { ...q, "PO Data": poDataStr } : q));
            setSelectedQuote(prev => (prev ? { ...prev, "PO Data": poDataStr } : prev));
            setShowRaisePOModal(false);
            setSuccessMsg("Purchase order sent");
            setTimeout(() => setSuccessMsg(""), 3000);
          }}
        />
      )}
    </>
  );

  if (recordRef) {
    const rq = selectedQuote?.["Quote Number"] === recordRef ? selectedQuote : recordRow;
    return (
      <div style={S.wrap}>
        <RecordPage
          {...(rq ? recordProps(rq) : { refNo: recordRef, q: null })}
          jobNotes={rq ? <JobNotes key={baseNumber(rq["Quote Number"])} q={rq} author={user?.fullName || changedByLabel} isMobile={isMobile} onFlagsChange={loadJobFlags} /> : null}
          loading={loading}
          isMobile={isMobile}
          onBack={backToLog}
          onOpenRecord={openOtherRecord}
          successMsg={successMsg}
        />
        {dialogs}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.wrap}>
      <div style={S.content}>
        {topContent}

        {/* Phones: no hero photo, straight to the work. */}
        {isMobile ? (
          <div style={{ marginBottom: 18 }}>{headlineBlock}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24, marginBottom: 24, alignItems: "stretch" }}>
            <div>{headlineBlock}</div>
            {heroBlock}
          </div>
        )}

        <Collapsible id="workflow" title="Workflow" isMobile={isMobile}>{workflowBlock}</Collapsible>
        <Collapsible id="attention" title={`Needs attention${attention.length ? ` (${attention.length})` : ""}`} isMobile={isMobile}>{attentionBlock}</Collapsible>

        <JobCalendarStrip quotes={quotes} suppliers={suppliers} isMobile={isMobile} onOpen={setSelectedQuote} />

        {/* Active / Completed / Closed tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {[
            // Counts quotes, not versions (superseded versions are hidden in
            // the list too), so the tab matches "x of x quotes".
            { key: "active", label: "Active", count: quotes.filter(q => listed(q) && ACTIVE_STATUSES.includes(q["Status"])).length },
            { key: "completed", label: "Completed", count: quotes.filter(q => listed(q) && COMPLETED_STATUSES.includes(q["Status"])).length },
            { key: "closed", label: "Closed", count: quotes.filter(q => listed(q) && CLOSED_STATUSES.includes(q["Status"])).length },
          ].map(tb => (
            <button
              key={tb.key}
              onClick={() => {
                setView(tb.key);
                setFilterStatus("All"); // last tab's status filter may not exist in the new one
              }}
              style={{
                padding: "9px 18px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 700,
                border: view === tb.key ? "none" : `1px solid ${C.border}`,
                background: view === tb.key ? C.brand : C.surface,
                color: view === tb.key ? "#fff" : C.textMuted,
              }}
            >
              {tb.label} <span style={{ opacity: 0.75, fontWeight: 500 }}>({tb.count})</span>
            </button>
          ))}
        </div>

        {view === "closed" && rejectedRows.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "10px 14px", marginBottom: 14, background: "#FFF8E6", border: "1px solid #F0D98A", borderRadius: 12, fontSize: 13, color: "#1a1a2e" }}>
            <span>{rejectedRows.length} quote{rejectedRows.length === 1 ? " is" : "s are"} still marked <strong>Rejected</strong> (the old name for Lost).</span>
            <button type="button" onClick={() => setConvertRejected({ rows: rejectedRows, done: [], busy: false })} style={{ minHeight: 36, padding: "0 14px", borderRadius: 8, border: "none", background: "#1D3461", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Convert Rejected to Lost…</button>
          </div>
        )}

        {/* Messages */}
        {successMsg && <div style={S.successBanner}>✓ {successMsg}</div>}
        {error && (
          <div style={S.errorBanner}>
            <strong>Error loading quotes:</strong> {error}
            <br />
            <small>Check your REACT_APP_GOOGLE_SHEETS_ID and REACT_APP_GOOGLE_API_KEY environment variables.</small>
          </div>
        )}

        {/* Controls */}
        <div style={S.controls}>
          <input
            style={S.input}
            placeholder="🔍  Search quotes, clients, applications…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select style={S.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option>All</option>
            {statusesOfView(view).filter(s => view !== "closed" || quotes.some(q => q["Status"] === s)).map(s => <option key={s}>{s}</option>)}
          </select>
          <select style={S.select} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option>All</option>
            <option>Industrial</option>
            <option>Automotive</option>
          </select>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMuted, whiteSpace: "nowrap", cursor: "pointer", minHeight: isMobile ? 36 : undefined }}>
            <input type="checkbox" checked={showTests} onChange={e => toggleShowTests(e.target.checked)} style={{ width: 15, height: 15 }} />
            Show test quotes
          </label>
          {isMobile ? (
            // Phones: one small line, e.g. "3 quotes · updated just now ↻".
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", fontSize: 12, color: C.textMuted }}>
              <span>{filtered.length === viewQuotes.length ? `${viewQuotes.length} quote${viewQuotes.length !== 1 ? "s" : ""}` : `${filtered.length} of ${viewQuotes.length} quotes`} · {loading ? "loading…" : (updatedAgo || "").replace(/^Updated/, "updated")}</span>
              <button onClick={() => load()} disabled={loading} aria-label="Refresh now" style={{ width: 40, height: 36, border: "none", background: "none", color: C.brand, fontSize: 18, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>↻</button>
            </div>
          ) : (
            <>
          <span style={S.resultsCount}>
            {filtered.length} of {viewQuotes.length} quotes
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMuted, whiteSpace: "nowrap" }}>
            <button
              onClick={() => load()}
              disabled={loading}
              title="Refresh now"
              aria-label="Refresh now"
              style={{ width: 34, height: 34, borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.brand, fontSize: 16, fontWeight: 700, cursor: loading ? "default" : "pointer", lineHeight: 1 }}
            >↻</button>
            {loading ? "Loading…" : updatedAgo}
          </span>
            </>
          )}
        </div>

        {/* Quotes: table on desktop, card list on mobile */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: C.textMuted }}>
            Loading the Quote Log…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: C.textMuted }}>
            {quotes.length === 0
              ? "No quotes logged yet. Generate your first quote and save it to the log."
              : "No quotes match your filters."}
          </div>
        ) : isMobile ? (
          <div>
            {filtered.map((q, i) => {
              return (
                <div key={q["Quote Number"] || i} id={`quote-row-${q["Quote Number"]}`}>
                  <div onClick={() => setSelectedQuote(q)} style={{
                    background: isJustSaved(q) ? JUST_SAVED_BG : C.surface, border: `1px solid ${isJustSaved(q) ? "#27AE60" : C.border}`, borderRadius: 16,
                    padding: 14, marginBottom: 10, boxShadow: "0 2px 10px rgba(20,30,70,0.05)", cursor: "pointer",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{fmt(q["Quote Number"])}<VersionTag qn={q["Quote Number"]} /><TestTag q={q} /></div>
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                          <StatusBadge status={q["Status"] || "Draft"} small />
                          {lossLine(q)}
                          {nextStepLine(q)}
                          {poDueTag(q)}
                        </div>
                        <span aria-hidden="true" style={{ fontSize: 22, lineHeight: "20px", color: C.textFaint, paddingLeft: 4 }}>›</span>
                      </div>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12.5, color: C.textMuted }}>
                      {fmt(q["Client Name"])}{q["Category"] && <> · <SectorPill category={q["Category"]} size="sm" /></>}
                      {q["Application"] ? ` · ${q["Application"]}` : ""}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 17, fontWeight: 800, color: C.text }}>
                      {fmt(q["Total (ex VAT)"])} <span style={{ fontSize: 11, fontWeight: 600, color: C.textMuted }}>ex VAT</span>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 11, color: C.textFaint }}>Updated {fmtShortDate(q["Date Updated"])}</div>
                  </div>

                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 24px rgba(20,30,70,0.04)" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {[
                      ["Quote Number", "Quote #"],
                      ["Application", "Application"],
                      ["Category", "Type"],
                      ["Total (ex VAT)", "Total ex VAT"],
                      ["Status", "Status"],
                      ...(view === "closed" ? [["Loss Reason", "Lost reason"]] : []),
                      ["Date Updated", "Updated"],
                    ].map(([key, label]) => (
                      <th
                        key={key}
                        style={{ ...S.th, cursor: "pointer", color: sortKey === key ? C.brand : S.th.color }}
                        onClick={() => sortHeader(key)}
                        aria-sort={sortKey === key ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
                      >
                        {label}<SortIcon col={key} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.flatMap((q, i) => {
                    const zebra = i % 2 === 0 ? C.surface : "#FAFBFE";

                    // The whole row opens the detail panel (home for all actions).
                    const mainRow = (
                      <tr
                        key={q["Quote Number"] || i}
                        id={`quote-row-${q["Quote Number"]}`}
                        onClick={() => setSelectedQuote(q)}
                        style={{ background: isJustSaved(q) ? JUST_SAVED_BG : zebra, boxShadow: isJustSaved(q) ? `inset 3px 0 0 #27AE60` : "none", cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#F0F3FF"}
                        onMouseLeave={e => e.currentTarget.style.background = isJustSaved(q) ? JUST_SAVED_BG : zebra}
                      >
                        <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                          <div style={{ fontWeight: 700, color: C.text }}>{fmt(q["Quote Number"])}<VersionTag qn={q["Quote Number"]} /><TestTag q={q} /></div>
                          <div style={{ fontSize: 12, color: C.textMuted, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }} title={q["Client Name"] || ""}>{fmt(q["Client Name"])}</div>
                        </td>
                        <td style={{ ...S.td, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: C.textMuted }} title={q["Application"] || ""}>
                          {fmt(q["Application"])}
                        </td>
                        <td style={S.td} title={q["Category"] || ""}>
                          {q["Category"] ? <SectorPill category={q["Category"]} style={{ maxWidth: 150 }} /> : fmt(q["Category"])}
                        </td>
                        <td style={{ ...S.td, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {fmt(q["Total (ex VAT)"])}
                        </td>
                        <td style={S.td}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                            <StatusBadge status={q["Status"] || "Draft"} small />
                            {lossLine(q)}
                            {nextStepLine(q)}
                            {poDueTag(q)}
                          </div>
                        </td>
                        {view === "closed" && (
                          <td style={{ ...S.td, maxWidth: 260 }}>
                            {lossReasonOf(q) && <div style={{ fontWeight: 600, color: C.text }}>{lossReasonOf(q)}</div>}
                            {q["Loss Note"] && <div style={{ fontSize: 12, color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={q["Loss Note"]}>{q["Loss Note"]}</div>}
                          </td>
                        )}
                        <td style={{ ...S.td, whiteSpace: "nowrap", color: C.textMuted }} title={q["Date Updated"] || ""}>{fmtShortDate(q["Date Updated"])}</td>
                      </tr>
                    );

                    return [mainRow];
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detail Panel: a snapshot of the quote and its next step. The full
            record (client, job, financials, PO, documents, history) is on
            the record page: Open → /record/:ref. */}
        {selectedQuote && (() => {
          const q = selectedQuote;
          const qn = q["Quote Number"];
          const detailStateJson = parseStateJson(q);
          const isSuperseded = q["Is Current"] === "No";
          const sec = { ...S.actionBtn, padding: "9px 8px", fontSize: 12.5, width: "100%", minHeight: isMobile ? 42 : 36 };
          const contractReady = q["Status"] === "Accepted" && !isAutomotive(q) && contractStatus[qn] !== "pending" && !isSuperseded;
          const primary = primaryAction(q);
          const amend = amendInfo(q);
          const versions = quotes.filter(x => baseNumber(x["Quote Number"]) === baseNumber(qn)).sort((x, y) => versionOf(x["Quote Number"]) - versionOf(y["Quote Number"]));
          const openReadOnly = () => onOpenReadOnly({ quote: q, stateJson: detailStateJson, onAmend: amend.run || null, amendLabel: amend.label === "Amend" ? "Amend to make changes" : (amend.label || ""), blockedReason: amend.reason || "" });
          const statusOptions = isSuperseded ? [] : manualTransitions(q);
          const stale = stalePending(q);
          const fullScope = detailStateJson ? resolveClientScope(detailStateJson) : "";
          const short = n => String(n || "").split("-").pop();
          const latest = isSuperseded ? latestOf(q) : null;
          const pill = { display: "inline-block", fontSize: 11.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999 };
          const sectionLabel = { fontSize: 11, fontWeight: 700, color: "#5a6a7a", textTransform: "uppercase", letterSpacing: "0.05em" };

          return (
          <>
            <div style={S.overlay} onClick={() => setSelectedQuote(null)} />
            <div style={S.panel}>
              <button style={S.panelClose} onClick={() => setSelectedQuote(null)}>✕</button>

              {isJustSaved(q) && (
                <div role="status" style={{ marginBottom: 14, marginRight: 24, padding: "12px 14px", background: JUST_SAVED_BG, border: "1.5px solid #27AE60", borderRadius: 10, color: "#0D5C36", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ fontSize: 18, lineHeight: 1, fontWeight: 800 }}>✓</div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14 }}>{justSaved.unchanged ? "Already saved as" : "Saved as"} {qn}{justSaved.unchanged ? " and unchanged" : ""} · {q["Status"] || "Draft"}</div>
                    {justSaved.sendNote && <div style={{ marginTop: 3, fontSize: 12, color: "#2E6B47" }}>{justSaved.sendNote}</div>}
                  </div>
                </div>
              )}

              {/* Ref as written (AUT-2026-046b), large and centred, with its
                  version and whether it's the current one. */}
              <div style={{ textAlign: "center", padding: "2px 28px 12px", marginBottom: 12, borderBottom: "1px solid #eef0f6" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#1a1a2e", letterSpacing: "-0.01em", lineHeight: 1.15, overflowWrap: "anywhere" }}>
                  {qn}<TestTag q={q} />
                </div>
                <div style={{ marginTop: 6 }}>
                  {isSuperseded
                    ? <span style={{ ...pill, background: "#F3F4F6", color: "#4B5563" }}>
                        v{versionOf(qn)} · superseded{latest && <>, current is <button onClick={() => setSelectedQuote(latest)} style={{ border: "none", background: "none", padding: 0, color: "#6D28D9", fontWeight: 700, cursor: "pointer", textDecoration: "underline", fontSize: 11.5 }}>{short(latest["Quote Number"])}</button></>}
                      </span>
                    : <span style={{ ...pill, background: "#F3E8FF", color: "#7C3AED" }}>v{versionOf(qn)} · current</span>}
                </div>
                {q["Amendment Of"] && <div style={{ marginTop: 6, fontSize: 12, color: "#8a95a3" }}>Amends {short(q["Amendment Of"])}</div>}
                {(() => { const has = clientHasLabel(versionClientHas(q, quotes, sendLog)); return has && <div style={{ marginTop: 4, fontSize: 12, color: "#92400E", fontWeight: 600 }}>{has}</div>; })()}
              </div>

              {/* Price and status */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <div style={{ fontSize: 30, fontWeight: 800, color: "#1a1a2e", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{fmt(q["Total (ex VAT)"])}</div>
                  <div style={{ fontSize: 11, color: "#8ab0cc" }}>ex VAT</div>
                </div>
                <StatusBadge status={q["Status"] || "Draft"} superseded={isSuperseded} />
                {lossLine(q)}
              </div>

              {/* Actions: one row of equal buttons. The next step's is solid
                  blue (it's next in line); Job Record, PDF and More are
                  outlined. Long labels wrap rather than widen a button. */}
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                {(() => {
                  const base = { width: "100%", minWidth: 0, minHeight: 44, padding: "6px 4px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, lineHeight: 1.2, cursor: "pointer", fontFamily: "inherit", boxSizing: "border-box" };
                  const outline = { ...base, background: C.surface, color: C.text, border: `1px solid ${C.border}` };
                  const buttons = [
                    primary && <button key="primary" style={{ ...base, background: C.brand, color: "#fff", border: `1px solid ${C.brand}`, opacity: primary.disabled ? 0.6 : 1 }} disabled={!!primary.disabled} onClick={primary.run}>{primary.label}</button>,
                    <button key="record" style={outline} onClick={() => openRecord(q)} title="The full record: client, job, financials, suppliers, documents and history">Job Record</button>,
                    detailStateJson && <button key="pdf" style={outline} onClick={() => printQuotePDF(q)}>PDF</button>,
                    detailStateJson && <button key="more" style={{ ...outline, background: moreOpen ? "#F0F3FF" : C.surface }} aria-expanded={moreOpen} onClick={() => setMoreOpen(o => !o)}>More {moreOpen ? "▴" : "▾"}</button>,
                  ].filter(Boolean);
                  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${buttons.length}, minmax(0, 1fr))`, gap: 6 }}>{buttons}</div>;
                })()}
                {detailStateJson && moreOpen && (
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                    {statusOptions.length > 0 && (
                      <select aria-label="Change status" style={{ ...S.statusSelect, minHeight: sec.minHeight }} value="" disabled={updatingStatus} onChange={e => e.target.value && handleStatusChange(q, e.target.value)}>
                        <option value="">{updatingStatus ? "Saving…" : `Change status (${q["Status"] || "Draft"})`}</option>
                        {statusOptions.map(s => <option key={s} value={s}>{statusActionLabel(s)}</option>)}
                      </select>
                    )}
                    {amend.run
                      ? <button style={{ ...sec, ...S.amendBtn, padding: "9px 8px", fontSize: 12.5, width: "100%", minHeight: sec.minHeight }} onClick={() => { if (isSuperseded) { handleAmendFromVersion(q); setSelectedQuote(null); } else if (handleAmend(q)) setSelectedQuote(null); }}>{amend.label}</button>
                      : <div style={{ fontSize: 11.5, color: "#8a95a3", lineHeight: 1.35 }}>{amend.reason}</div>}
                    {/* Send to Client again (Sent and later) and Send Contract
                        when they aren't the primary: still available here. */}
                    {canSend(q) && primary?.key !== "send" && (
                      <button style={{ ...sec, opacity: sendingEmail[qn] || savingContact ? 0.6 : 1 }} disabled={!!sendingEmail[qn] || savingContact} onClick={() => openSendConfirm(q)}>{savingContact ? SAVING_CONTACT : sendingEmail[qn] ? "Sending…" : "Send to Client"}</button>
                    )}
                    {contractReady && primary?.key !== "contract" && (
                      <button style={{ ...sec, ...S.contractBtn, padding: "9px 8px", fontSize: 12.5, width: "100%", minHeight: sec.minHeight, opacity: savingContact ? 0.6 : 1 }} disabled={savingContact} onClick={() => handleSendContract(qn)}>{savingContact ? SAVING_CONTACT : "Send Contract"}</button>
                    )}
                    {!isSuperseded && q["Status"] === "Draft" && (
                      <button style={{ ...sec, opacity: savingContact ? 0.6 : 1 }} disabled={updatingStatus || savingContact} onClick={() => handleMarkAsSent(q)} title="Status to Sent and Date Sent set. Nothing is emailed.">{savingContact ? SAVING_CONTACT : "Mark as sent (no email)"}</button>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <button style={sec} onClick={() => openQuotePage(q)}>View Quote ↗</button>
                      <button style={sec} onClick={() => printJobSheet(q)}>Job Sheet</button>
                    </div>
                    {!isSuperseded && (
                      <button style={sec} onClick={() => { startContactEdit(q, detailStateJson); openRecord(q, "client"); }}>Edit contact details</button>
                    )}
                    {onOpenReadOnly && <button style={sec} onClick={openReadOnly} title="The saved quote in its calculator, read only">Open in calculator (read only)</button>}
                    {onCopyQuote && (
                      // A new quote from this one's set-up. Not an amendment:
                      // new number on save, this quote untouched.
                      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                        <button style={sec} onClick={() => { onCopyQuote({ originalQuote: q, stateJson: detailStateJson, copyDiscount }); setSelectedQuote(null); }}>
                          ⧉ Copy as new quote
                        </button>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12, color: hasDiscount(detailStateJson) ? "#4a5568" : "#aab2c0", cursor: hasDiscount(detailStateJson) ? "pointer" : "default" }}>
                          <input type="checkbox" checked={copyDiscount} disabled={!hasDiscount(detailStateJson)} onChange={e => setCopyDiscount(e.target.checked)} />
                          Copy discount{hasDiscount(detailStateJson) ? "" : " (none on this quote)"}
                        </label>
                        <div style={{ fontSize: 11, color: "#8a95a3", marginTop: 4 }}>
                          Client, vehicle, dates and travel miles are cleared. Saves under a new number.
                        </div>
                      </div>
                    )}
                    {versions.length > 1 && (
                      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                        <div style={{ ...sectionLabel, marginBottom: 6 }}>Versions</div>
                        {versions.map(v => {
                          const isThis = v["Quote Number"] === qn;
                          return (
                            <button key={v["Quote Number"]} disabled={isThis} onClick={() => setSelectedQuote(v)} style={{ display: "flex", justifyContent: "space-between", gap: 8, width: "100%", border: "none", background: isThis ? "#F4F6FC" : "none", borderRadius: 6, padding: "6px 8px", cursor: isThis ? "default" : "pointer", fontSize: 12.5, color: "#1a1a2e", textAlign: "left" }}>
                              <span><strong>v{versionOf(v["Quote Number"])}</strong> · {v["Quote Number"]}{isThis ? " (viewing)" : ""}</span>
                              <span style={{ color: "#5a6a7a", whiteSpace: "nowrap" }}>{fmt(v["Total (ex VAT)"])} · {v["Is Current"] === "No" ? "superseded" : (v["Status"] || "Draft")}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* A contract / RAMS out to an old address: flagged here, fixed
                  on the record page (Documents). */}
              {stale.length > 0 && (
                <button onClick={() => openRecord(q, "documents")} style={{ marginTop: 14, width: "100%", textAlign: "left", padding: "10px 12px", background: "#FFF7E6", border: "1px solid #F5A623", borderRadius: 10, color: "#7B5800", fontSize: 12.5, fontWeight: 700, cursor: "pointer", lineHeight: 1.4 }}>
                  ⚠ {stale.map(s => s.docType === "contract" ? "Contract" : "RAMS").join(" and ")} sent to an old email address. Void and resend on the Job Record ›
                </button>
              )}

              {/* Where this job is up to: each lifecycle step, ticked once done;
                  a step this job doesn't need (na) gets a grey dash, not a tick. */}
              {(() => {
                // Automotive: deposit invoice instead of a contract, balance on
                // collection (no Invoiced step), optional RAMS on site.
                const auto = isAutomotive(q);
                const ORDER = auto ? ["Draft", "Sent", "Accepted", DEPOSIT_PENDING, DEPOSIT_PAID, "Complete"] : ["Draft", "Sent", "Accepted", "Contracted", "Invoiced", "Complete"];
                const status = q["Status"] === "Amended" ? "Draft" : (q["Status"] || "Draft");
                if (isLostStatus(status) || !ORDER.includes(status)) return null;
                const at = ORDER.indexOf(status);
                const reached = st => at >= ORDER.indexOf(st);
                const onSite = isOnSiteQuote(q);
                const { start } = getJobDates(q);
                const po = parsePOData(q);
                const rows = [
                  { label: "Quote sent", done: reached("Sent"), note: q["Date Sent"] ? q["Date Sent"] : "" },
                  { label: "Accepted by client", done: reached("Accepted") },
                  ...(auto
                    ? [{ label: "Deposit invoice sent", done: reached(DEPOSIT_PENDING) }, { label: "Deposit paid", done: reached(DEPOSIT_PAID) }]
                    : [{ label: "Service Contract signed", done: reached("Contracted") }]),
                  !onSite ? { label: "RAMS", na: true, note: "not needed (in-unit)" }
                    : auto ? { label: "RAMS signed", done: ramsOf(qn) === "signed", na: ramsOf(qn) !== "signed" && ramsOf(qn) !== "pending", note: ramsOf(qn) === "pending" ? "awaiting signature" : ramsOf(qn) === "signed" ? "" : "optional" }
                    : { label: "RAMS signed", done: ramsOf(qn) === "signed", note: ramsFlagOnly(qn) ? "in GoodSign" : reached("Contracted") ? "" : "after the contract" },
                  { label: "Job date set", done: !!start, note: start ? fmtDay(start) : "" },
                  ...(reached("Accepted") ? [{ label: "Dry ice order confirmed", done: po.poStatus === "confirmed" }] : []),
                  ...(auto ? [] : [{ label: "Invoiced", done: reached("Invoiced") }]),
                  { label: auto ? "Complete (balance paid on collection)" : "Complete", done: reached("Complete") },
                ];
                return (
                  <div style={{ marginTop: 18, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ ...sectionLabel, marginBottom: 6 }}>Progress</div>
                    {rows.map(r => (
                      <div key={r.label} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12.5, padding: "3px 0", color: r.done && !r.na ? C.text : C.textMuted }}>
                        <span style={{ width: 16, textAlign: "center", fontWeight: 800, color: r.na ? "#AEB6C4" : r.done ? "#27AE60" : "#C5CBD8" }} aria-label={r.na ? "not needed" : r.done ? "done" : "to do"}>{r.na ? "—" : r.done ? "✓" : "○"}</span>
                        <span style={{ flex: 1 }}>{r.label}</span>
                        {r.note && <span style={{ fontSize: 11, color: C.textFaint }}>{r.note}</span>}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Scope Summary, folded by default (the full client scope from
                  the quote's state; the column is cut at 200 characters). */}
              {(fullScope || q["Scope Summary"]) && (
                <div style={{ marginTop: 12, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                  <button onClick={() => setScopeOpen(o => !o)} aria-expanded={scopeOpen} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", padding: "10px 12px", border: "none", background: "none", cursor: "pointer", ...sectionLabel }}>
                    <span>Scope Summary</span><span aria-hidden="true">{scopeOpen ? "▴" : "▾"}</span>
                  </button>
                  {scopeOpen && (fullScope
                    ? <div style={{ fontSize: 12.5, color: "#333", lineHeight: 1.55, padding: "0 12px 12px" }} dangerouslySetInnerHTML={{ __html: scopeTextToHTML(fullScope) }} />
                    : <div style={{ fontSize: 12.5, color: "#333", lineHeight: 1.55, padding: "0 12px 12px", whiteSpace: "pre-line" }}>{q["Scope Summary"]}</div>)}
                </div>
              )}
            </div>
          </>
          );
        })()}

        {dialogs}
      </div>
    </div>
  );
}
